// Screen builders for menu / mode select / settings / stats / about.

import type { SfxName, SfxParams } from '../audio/audio';
import { SFX_BANKS } from '../audio/audio';
import { APP_NAME, APP_VERSION } from '../config';
import type { Action } from '../input/input';
import { DEFAULT_KEYMAP, keyLabel } from '../input/input';
import { CHANGELOG } from '../changelog';
import { LEGAL_CONTACT, LEGAL_UPDATED, PRIVACY, TERMS } from '../legal';
import type { LegalSection } from '../legal';
import { ALL_MODES, formatTime, getMode } from '../modes/modes';
import { getAccount, login, logout, register, requestReset, resetPassword } from '../net/auth';
import { buildSnapshot, prepareImage, submitFeedback } from '../net/feedback';
import { fetchLeaderboard, formatLeaderboardValue, LEADERBOARD_MODES } from '../net/leaderboard';
import type { SubmitResult } from '../net/leaderboard';
import { getPalette, PALETTES } from '../render/palettes';
import { glyphFor, GLYPH_H, GLYPH_W } from '../render/pixel-font';
import type { ThemePalette } from '../render/palettes';
import { ACHIEVEMENTS, CATEGORIES, categoryFor } from '../state/achievements';
import type { AchievementCategory } from '../state/achievements';
import type { ProfileRecord } from '../state/storage';
import { DEFAULT_SETTINGS, DEFAULT_STATS } from '../state/storage';
import type { PersistedStats, Settings } from '../state/storage';
import type { ModeId, ModeResultDetails } from '../types';
import { alertModal, clearScreen, confirmModal, decoratePanel, el, feedbackModal, icon, registerScreenCleanup, screenTitle, select, slider, successModal, toggle } from './ui';

/** Renders text as a row of pixel-font glyphs where each '#' bit is a
 *  fully-styled tetrimino cell (swatchCss using the active palette +
 *  cell style). Cells stagger in via the surrounding CSS based on the
 *  `kind` class. Used by the countdown + end-of-match overlays.
 *
 *  Color override: kinds 'count' and 'go' force every cell to use the
 *  palette accent2 (blue/cyan family on most themes) so the countdown
 *  reads as one cohesive blue glyph instead of a multi-piece confetti. */
export function buildPixelText(
  text: string,
  palette: ThemePalette,
  cellStyle: string,
  cellPx: number,
  kind: string,
): HTMLElement {
  const root = el('div', { class: `pixel-text pixel-text-${kind}` });
  const chars = text.toUpperCase().split('');
  const pieceOrder: ('I' | 'O' | 'T' | 'S' | 'Z' | 'J' | 'L')[] = ['I', 'O', 'T', 'S', 'Z', 'J', 'L'];
  // Countdown / GO render in white (solid brand-font feel); the end-
  // of-match overlays rotate through piece colors per-character so
  // each letter reads as a chunky tetrimino block.
  const forceColor = (kind === 'count' || kind === 'go') ? '#ffffff' : null;
  let cellSeq = 0;
  chars.forEach((ch, charIdx) => {
    const glyph = glyphFor(ch);
    const glyphEl = el('div', { class: 'pixel-glyph' });
    glyphEl.style.gridTemplateColumns = `repeat(${GLYPH_W}, ${cellPx}px)`;
    glyphEl.style.gridTemplateRows = `repeat(${GLYPH_H}, ${cellPx}px)`;
    const pieceColor = forceColor ?? palette.pieces[pieceOrder[charIdx % pieceOrder.length]!];
    for (let row = 0; row < GLYPH_H; row++) {
      for (let col = 0; col < GLYPH_W; col++) {
        const filled = glyph[row]![col] === '#';
        if (filled) {
          const cellEl = el('div', { class: 'pixel-cell filled' });
          // Per-cell offset / rotation seeded by grid position so the
          // 'tetriminos falling into place' animation has variety across
          // the glyph (top cells drop from further above, edges rotate
          // in from the side). Deterministic — no Math.random() — so
          // re-renders look identical for the same character.
          const fallY = -(60 + row * 8 + col * 2);
          const rot = col % 2 === 0 ? -90 : 90;
          const skewX = (col - 2) * 6;
          cellEl.setAttribute('style',
            `${swatchCss(pieceColor, cellStyle)} `
            + `--fall-y:${fallY}px; --rot:${rot}deg; --skew-x:${skewX}px; `
            + `animation-delay: ${cellSeq * 14}ms;`);
          glyphEl.appendChild(cellEl);
          cellSeq++;
        } else {
          glyphEl.appendChild(el('div', { class: 'pixel-cell' }));
        }
      }
    }
    root.appendChild(glyphEl);
  });
  return root;
}

/** Walk the DOM, find every .brand-mark, and rebuild its 2x2 cells
 *  with the current palette + cell style. Called whenever the user
 *  changes palette or tetrimino style so the brand mark updates in
 *  place without re-rendering the whole panel. */
export function refreshBrandMarks(h: ScreenHost): void {
  const palette = getPalette(h.settings.paletteId, h.settings.customPalettes);
  const style = h.settings.render.rgbCellStyle;
  const types: ('I' | 'T' | 'S' | 'L')[] = ['I', 'T', 'S', 'L'];
  document.querySelectorAll('.brand-mark').forEach((mark) => {
    while (mark.firstChild) mark.removeChild(mark.firstChild);
    for (const t of types) {
      mark.appendChild(el('div', {
        class: 'brand-mark-cell',
        style: swatchCss(palette.pieces[t], style),
      }));
    }
  });
}

/** The persistent brand strip at the top of every panel. Pulls four
 *  piece colors from the active palette and the current tetrimino-style
 *  treatment from settings, so the mark cells next to "settle" look
 *  like four tiny tetrimino chips in the player's chosen aesthetic. */
export function brandHeader(h: ScreenHost): HTMLElement {
  const palette = getPalette(h.settings.paletteId, h.settings.customPalettes);
  const style = h.settings.render.rgbCellStyle;
  const wrap = el('div', { class: 'brand-header' });
  wrap.appendChild(el('span', { class: 'brand-name' }, ['settle']));
  const mark = el('div', { class: 'brand-mark', 'aria-hidden': 'true' });
  // Four piece types chosen for visual distinction in the 2x2 mark grid.
  const types: ('I' | 'T' | 'S' | 'L')[] = ['I', 'T', 'S', 'L'];
  for (const t of types) {
    mark.appendChild(el('div', {
      class: 'brand-mark-cell',
      style: swatchCss(palette.pieces[t], style),
    }));
  }
  wrap.appendChild(mark);
  // Two-line tagline so 'a modern,' and 'relaxing tetris-like' stack
  // visually like a sub-banner under the mark instead of trailing as
  // one long string.
  const tagline = el('span', { class: 'brand-tagline' }, [
    'a modern,',
    el('br', {}),
    'relaxing tetris-like',
  ]);
  wrap.appendChild(tagline);
  return wrap;
}

type Nav = (
  | { type: 'menu' }
  | { type: 'modes' }
  | { type: 'settings'; tab?: 'video' | 'audio' | 'handling' | 'controls' | 'themes' | 'general' }
  | { type: 'stats' }
  | { type: 'about' }
  | { type: 'play'; mode: ModeId }
  | { type: 'versus' }
  | { type: 'profile-select' }
  | { type: 'custom-palettes' }
  | { type: 'results'; mode: ModeId; result: ModeResultDetails }
  | { type: 'results-return' }
  | { type: 'leaderboard' }
  | { type: 'changelog' }
  | { type: 'account' }
  | { type: 'legal'; tab?: 'terms' | 'privacy' }
);

export interface ScreenHost {
  host: HTMLElement;
  navigate: (nav: Nav) => void;
  settings: Settings;
  saveSettings: () => void;
  stats: PersistedStats;
  saveStats: () => void;
  beginRebind: (action: Action, slotIndex: number) => void;
  onPaletteChange: () => void;
  onAudioChange: () => void;
  onVideoChange: () => void;
  onHandlingChange: () => void;
  onMusicVariantChange: () => void;
  testSfx: (name: SfxName) => void;
  /** UI navigation feedback — triggered by enableKeyboardMenuNav so list
   *  focus shifts and selections have a consistent menu-bleep sound. */
  playMenuSfx: (kind: 'move' | 'select' | 'back') => void;
  /** Current profile name to display in the menu chip; null if unset. */
  activeProfileName: string | null;
  /** Active profile's stable id for leaderboard identity. Null when no
   *  profile is active OR the session is a guest (guests can browse the
   *  board but never submit). */
  activeProfileId: string | null;
  /** Optional: triggered when player chooses to restart from the results screen. */
  lastMode: ModeId | null;
}

export function buildMenu(h: ScreenHost): void {
  clearScreen(h.host);
  const panel = el('div', { class: 'panel' });
  decoratePanel(panel, `settle // ${APP_VERSION}`);
  // Main menu shows the brand header alone — no sub-screen title beneath.
  panel.appendChild(brandHeader(h));
  // Identity chip — shows the signed-in ACCOUNT (the owned competition
  // identity), not the device-local save profile. Signed out, it
  // invites sign-in via the a/b account modal.
  {
    const acctChip = getAccount();
    const profileChip = el('button', { class: 'profile-chip', type: 'button', title: acctChip ? 'account' : 'sign in' }, [
      el('span', { class: 'profile-chip-icon' }, [icon('profile')]),
      el('span', { class: 'profile-chip-label' }, [acctChip ? 'signed in as' : 'not signed in']),
      el('span', { class: 'profile-chip-name' }, [acctChip ? acctChip.username : '']),
      acctChip ? null : el('span', { class: 'profile-chip-swap' }, ['sign in']),
    ]);
    profileChip.addEventListener('click', () => {
      h.playMenuSfx('select');
      h.navigate({ type: 'account' });
    });
    panel.appendChild(profileChip);
  }
  const list = el('div', { class: 'menu-list' });
  // Each menu item: icon glyph + label + tagline. Cohesive visual rhythm
  // across the whole menu — every entry has the same anatomy. Right column
  // hints removed at the user's request so the descriptions can breathe.
  // 'battle' is shipped as wired-up code but disabled in the main menu UI
  // for now while we focus on polishing the solo experience.
  const items: { icon: string; label: string; desc: string; nav: Nav; disabled?: boolean }[] = [
    { icon: 'gaming',         label: 'solo',     desc: 'choose from one of six solo gameplay modes',         nav: { type: 'modes' } },
    { icon: 'versus-duel',    label: 'bot battle', desc: 'go head-to-head against the computer',              nav: { type: 'versus' }, disabled: true },
    { icon: 'swords-crossed', label: 'pvp',      desc: 'go head-to-head against other players',               nav: { type: 'versus' }, disabled: true },
    { icon: 'trophy-solid',   label: 'leaderboard', desc: 'see the top runs for every mode',                  nav: { type: 'leaderboard' } },
    { icon: 'settings',       label: 'settings', desc: 'tune the look, sound, and feel of the game',          nav: { type: 'settings' } },
    { icon: 'stats',          label: 'stats',    desc: 'browse your records, totals, and achievements',       nav: { type: 'stats' } },
    { icon: 'help',           label: 'help',     desc: 'learn how to play and pick up some strategies',       nav: { type: 'about' } },
  ];
  items.forEach((it, i) => {
    const cls = `menu-item${it.disabled ? ' menu-item-disabled' : ''}`;
    const children: (HTMLElement | string)[] = [
      el('div', { class: 'menu-icon' }, [icon(it.icon)]),
      el('div', { class: 'menu-body' }, [
        el('div', { class: 'menu-label' }, [it.label]),
        el('div', { class: 'menu-desc' }, [it.desc]),
      ]),
    ];
    if (it.disabled) {
      children.push(el('div', { class: 'menu-coming-soon' }, ['coming soon']));
    }
    const item = el('div', { class: cls, dataset: { nav: 'menu', idx: String(i) } }, children);
    if (!it.disabled) {
      item.addEventListener('click', () => { h.playMenuSfx('select'); h.navigate(it.nav); });
    }
    list.appendChild(item);
  });
  panel.appendChild(list);
  // Quiet footer row — feedback composer, account entry, and the live
  // presence counter (net/presence.ts updates every .settlers-online-count).
  const feedbackLink = el('button', { class: 'menu-footer-link', type: 'button' }, ['send feedback']);
  feedbackLink.addEventListener('click', () => {
    h.playMenuSfx('select');
    feedbackModal({ buildSnapshot, prepareImage, submit: (m, snap, img) => submitFeedback(m, snap, img) });
  });
  const acct = getAccount();
  const accountLink = el('button', { class: 'menu-footer-link', type: 'button' }, [
    acct ? 'sign out' : 'sign in',
  ]);
  accountLink.addEventListener('click', async () => {
    h.playMenuSfx('select');
    if (!getAccount()) {
      h.navigate({ type: 'account' });
      return;
    }
    const ok = await confirmModal({
      title: 'sign out?',
      body: 'leaderboard submissions and chat will need a sign-in again. local saves are unaffected.',
      confirmLabel: 'sign out',
      tone: 'primary',
    });
    if (!ok) return;
    await logout();
    h.navigate({ type: 'menu' });
  });
  panel.appendChild(el('div', { class: 'menu-footer spread' }, [
    feedbackLink,
    el('span', { class: 'settlers-online-count' }, ['']),
    accountLink,
  ]));
  h.host.appendChild(panel);
  const enabledItems = items.filter((it) => !it.disabled);
  enableKeyboardMenuNav(
    panel, '.menu-item:not(.menu-item-disabled)',
    enabledItems.map((it) => () => h.navigate(it.nav)),
    undefined,
    { onMove: () => h.playMenuSfx('move'), onSelect: () => h.playMenuSfx('select') },
  );
}

export function buildModes(h: ScreenHost, backTo: 'menu' | 'results-return' = 'menu'): void {
  clearScreen(h.host);
  const goBack = () => h.navigate(backTo === 'menu' ? { type: 'menu' } : { type: 'results-return' });
  const panel = el("div", { class: "panel panel-wide" }); decoratePanel(panel, `settle // ${APP_VERSION}`);
  panel.appendChild(brandHeader(h)); panel.appendChild(screenTitle('solo'));
  const list = el('div', { class: 'menu-list' });
  // One icon per mode — consistent visual rhythm with the main menu.
  const MODE_ICONS: Record<string, string> = {
    marathon:  'star-solid',
    sprint40:  'sprint',
    ultra2min: 'clock-solid',
    cheese:    'trash-alt-solid',
    master:    'trophy-solid',
    zen:       'zen',
  };
  ALL_MODES.forEach((m, i) => {
    const pbKey = m.pbKey;
    const pbRaw = pbKey ? h.stats.pbs[pbKey] : undefined;
    const pbText = pbRaw != null
      ? (m.pbLowerBetter
        ? `pb ${formatTime(pbRaw)}`
        : `pb ${pbRaw.toLocaleString()}`)
      : 'no pb';
    const iconName = MODE_ICONS[m.id] ?? 'play';
    const item = el('div', { class: `menu-item${i === 0 ? ' focus' : ''}`, dataset: { nav: 'modes', idx: String(i) } }, [
      el('div', { class: 'menu-icon' }, [icon(iconName)]),
      el('div', { class: 'menu-body' }, [
        el('div', { class: 'menu-label' }, [m.name.toLowerCase()]),
        el('div', { class: 'menu-desc' }, [m.description]),
      ]),
      el('div', { class: 'right' }, [
        el('div', { class: m.pbKey ? 'pb' : '' }, [pbText]),
        el('div', { style: 'margin-top: 2px; color: var(--text-dim); font-size: 10px; letter-spacing: 0.16em;' }, [m.short]),
      ]),
    ]);
    item.addEventListener('click', () => { h.playMenuSfx('select'); h.navigate({ type: 'play', mode: m.id }); });
    list.appendChild(item);
  });
  panel.appendChild(list);
  const row = el('div', { class: 'row' }, [
    backButton(h, goBack),
  ]);
  panel.appendChild(row);
  h.host.appendChild(panel);
  enableKeyboardMenuNav(
    panel, '.menu-item',
    ALL_MODES.map((m) => () => h.navigate({ type: 'play', mode: m.id })),
    goBack,
    { onMove: () => h.playMenuSfx('move'), onSelect: () => h.playMenuSfx('select'), onBack: () => h.playMenuSfx('back') },
  );
}

export function buildSettings(h: ScreenHost, tab: 'video' | 'audio' | 'handling' | 'controls' | 'themes' | 'general' = 'general'): void {
  clearScreen(h.host);
  const panel = el("div", { class: "panel panel-wide panel-fixed" }); decoratePanel(panel, `settle // ${APP_VERSION}`);
  panel.appendChild(brandHeader(h)); panel.appendChild(screenTitle('settings'));
  const tabs = el('div', { class: 'tabs' });
  const TABS: { id: typeof tab; label: string }[] = [
    { id: 'video', label: 'video' },
    { id: 'audio', label: 'audio' },
    { id: 'handling', label: 'handling' },
    { id: 'controls', label: 'controls' },
    { id: 'themes', label: 'themes' },
    { id: 'general', label: 'general' },
  ];
  TABS.forEach((t) => {
    const b = el('button', { class: `tab${t.id === tab ? ' active' : ''}` }, [t.label]);
    b.addEventListener('click', () => {
      if (t.id !== tab) h.playMenuSfx('move');
      h.navigate({ type: 'settings', tab: t.id });
    });
    tabs.appendChild(b);
  });
  panel.appendChild(tabs);
  // Tab/Shift-Tab cycles through tabs, Esc returns to menu.
  const settingsKey = (e: KeyboardEvent) => {
    // Don't hijack if a form input has focus and the user is editing values.
    const target = e.target as HTMLElement | null;
    if (target && (target.tagName === 'INPUT' || target.tagName === 'SELECT' || target.tagName === 'TEXTAREA')) return;
    if (e.code === 'Tab') {
      e.preventDefault();
      const i = TABS.findIndex((t) => t.id === tab);
      const next = (i + (e.shiftKey ? -1 : 1) + TABS.length) % TABS.length;
      h.playMenuSfx('move');
      h.navigate({ type: 'settings', tab: TABS[next]!.id });
    } else if (e.code === 'Escape') {
      e.preventDefault();
      h.playMenuSfx('back');
      h.navigate({ type: 'menu' });
    }
  };
  window.addEventListener('keydown', settingsKey);
  registerScreenCleanup(() => window.removeEventListener('keydown', settingsKey));
  const content = el('div', { class: 'tab-content section' });
  switch (tab) {
    case 'general': fillGeneral(h, content); break;
    case 'video': fillVideo(h, content); break;
    case 'audio': fillAudio(h, content); break;
    case 'themes': fillThemes(h, content); break;
    case 'handling': fillHandling(h, content); break;
    case 'controls': fillControls(h, content); break;
  }
  panel.appendChild(content);
  enableSettingsKeyboardNav(content);
  // Per-tab reset is appended by each filler; global 'reset all
  // settings' lives on the general tab. The bottom row keeps just the
  // back button for consistency across tabs.
  const row = el('div', { class: 'row' }, [
    backButton(h, () => h.navigate({ type: 'menu' })),
  ]);
  panel.appendChild(row);
  h.host.appendChild(panel);
}

function fillGeneral(h: ScreenHost, content: HTMLElement): void {
  content.appendChild(toggle('pre-game countdown', h.settings.countdownEnabled, (v) => { h.settings.countdownEnabled = v; h.saveSettings(); }));
  content.appendChild(toggle('resume countdown', h.settings.resumeCountdownEnabled, (v) => { h.settings.resumeCountdownEnabled = v; h.saveSettings(); }));
  content.appendChild(toggle('show fps counter', h.settings.showFps, (v) => { h.settings.showFps = v; h.saveSettings(); }));
  content.appendChild(toggle('uncapped framerate', h.settings.highFramerate, (v) => { h.settings.highFramerate = v; h.saveSettings(); h.onVideoChange(); }));
  content.appendChild(toggle('show ghost piece', h.settings.render.showGhost, (v) => { h.settings.render.showGhost = v; h.saveSettings(); h.onVideoChange(); }));
  content.appendChild(toggle('show grid', h.settings.render.showGrid, (v) => { h.settings.render.showGrid = v; h.saveSettings(); h.onVideoChange(); }));
  content.appendChild(toggle('chat profanity filter', h.settings.chatFilter, (v) => { h.settings.chatFilter = v; h.saveSettings(); }, 'masks strong language in chat with symbols. mild words pass through.'));
  content.appendChild(slider({
    label: 'ghost piece opacity', min: 0, max: 1, step: 0.02, value: h.settings.render.ghostAlpha,
    format: (v) => `${(v * 100).toFixed(0)}%`,
    onChange: (v) => { h.settings.render.ghostAlpha = v; h.saveSettings(); h.onVideoChange(); },
  }));
  appendTabResetButton(content, h, 'general');
  // Global 'reset all settings' lives on the general tab so destructive
  // bulk operations sit alongside the rest of the housekeeping toggles.
  const resetAll = el('button', { class: 'btn ghost', style: 'margin-top: 10px' }, ['reset all settings to default']);
  resetAll.addEventListener('click', () => { void resetAllSettings(h); });
  content.appendChild(resetAll);
}

function fillVideo(h: ScreenHost, content: HTMLElement): void {
  // Render scale is intentionally NOT exposed for now: scaling the
  // canvas below 1 desyncs the viewport-rect math the DOM overlays
  // (trivia card etc.) position from, hiding them. The setting is
  // normalized to 1 at load (storage.ts) until that mapping is fixed.
  content.appendChild(toggle('post-processing', h.settings.postfx.enabled, (v) => {
    h.settings.postfx.enabled = v; h.saveSettings(); h.onVideoChange();
    // Re-render so the dependent sliders pick up their disabled state.
    while (content.firstChild) content.removeChild(content.firstChild);
    fillVideo(h, content);
  }, 'master switch for all the shader effects below.'));
  // Everything below depends on the master switch — collect and grey
  // out when disabled.
  const fxControls: HTMLElement[] = [];
  const fx = (elx: HTMLElement): HTMLElement => { fxControls.push(elx); return elx; };
  content.appendChild(fx(slider({
    label: 'bloom', min: 0, max: 1.5, step: 0.02, value: h.settings.postfx.bloom,
    format: (v) => `${(v * 100).toFixed(0)}%`,
    onChange: (v) => { h.settings.postfx.bloom = v; h.saveSettings(); h.onVideoChange(); },
  })));
  content.appendChild(fx(slider({
    label: 'bloom threshold', min: 0.1, max: 0.95, step: 0.01, value: h.settings.postfx.bloomThreshold,
    format: (v) => `${(v * 100).toFixed(0)}%`,
    onChange: (v) => { h.settings.postfx.bloomThreshold = v; h.saveSettings(); h.onVideoChange(); },
  })));
  content.appendChild(fx(slider({
    label: 'crt scanlines', min: 0, max: 1, step: 0.02, value: h.settings.postfx.crt,
    format: (v) => `${(v * 100).toFixed(0)}%`,
    onChange: (v) => { h.settings.postfx.crt = v; h.saveSettings(); h.onVideoChange(); },
  })));
  content.appendChild(fx(slider({
    label: 'crt curvature', min: 0, max: 0.3, step: 0.005, value: h.settings.postfx.curvature,
    format: (v) => `${(v * 100).toFixed(0)}%`,
    onChange: (v) => { h.settings.postfx.curvature = v; h.saveSettings(); h.onVideoChange(); },
  })));
  content.appendChild(fx(slider({
    label: 'chromatic aberration', min: 0, max: 1.5, step: 0.02, value: h.settings.postfx.chromatic,
    format: (v) => `${(v * 100).toFixed(0)}%`,
    onChange: (v) => { h.settings.postfx.chromatic = v; h.saveSettings(); h.onVideoChange(); },
  })));
  content.appendChild(fx(slider({
    label: 'film grain', min: 0, max: 1, step: 0.02, value: h.settings.postfx.grain,
    format: (v) => `${(v * 100).toFixed(0)}%`,
    onChange: (v) => { h.settings.postfx.grain = v; h.saveSettings(); h.onVideoChange(); },
  })));
  content.appendChild(fx(slider({
    label: 'vignette', min: 0, max: 1, step: 0.02, value: h.settings.postfx.vignette,
    format: (v) => `${(v * 100).toFixed(0)}%`,
    onChange: (v) => { h.settings.postfx.vignette = v; h.saveSettings(); h.onVideoChange(); },
  })));
  content.appendChild(fx(slider({
    label: 'glow lift', min: 0, max: 1, step: 0.02, value: h.settings.postfx.glow,
    format: (v) => `${(v * 100).toFixed(0)}%`,
    onChange: (v) => { h.settings.postfx.glow = v; h.saveSettings(); h.onVideoChange(); },
  })));
  content.appendChild(fx(slider({
    label: 'scanline size', min: 1, max: 6, step: 1, value: h.settings.postfx.scanlineSize,
    format: (v) => `${v}px`,
    onChange: (v) => { h.settings.postfx.scanlineSize = v; h.saveSettings(); h.onVideoChange(); },
  })));
  if (!h.settings.postfx.enabled) {
    for (const c of fxControls) c.classList.add('ctl-disabled');
  }
  appendTabResetButton(content, h, 'video');
}

function fillAudio(h: ScreenHost, content: HTMLElement): void {
  // Everything volume-related greys out while mute-all is on — the
  // sliders would be lying otherwise.
  const muted = h.settings.audio.muted;
  const mutable = (elx: HTMLElement): HTMLElement => {
    if (muted) elx.classList.add('ctl-disabled');
    return elx;
  };
  content.appendChild(mutable(slider({
    label: 'master', min: 0, max: 1, step: 0.01, value: h.settings.audio.master,
    format: (v) => `${(v * 100).toFixed(0)}%`,
    onChange: (v) => { h.settings.audio.master = v; h.saveSettings(); h.onAudioChange(); },
  })));
  content.appendChild(mutable(slider({
    label: 'music', min: 0, max: 1, step: 0.01, value: h.settings.audio.music,
    format: (v) => `${(v * 100).toFixed(0)}%`,
    onChange: (v) => { h.settings.audio.music = v; h.saveSettings(); h.onAudioChange(); },
  })));
  content.appendChild(mutable(slider({
    label: 'sound effects', min: 0, max: 1, step: 0.01, value: h.settings.audio.sfx,
    format: (v) => `${(v * 100).toFixed(0)}%`,
    onChange: (v) => { h.settings.audio.sfx = v; h.saveSettings(); h.onAudioChange(); },
  })));
  // Mute-all overrides every bus; flipping it re-renders the tab so
  // dependent controls pick up their disabled state.
  content.appendChild(toggle('mute all', h.settings.audio.muted, (v) => {
    h.settings.audio.muted = v; h.saveSettings(); h.onAudioChange();
    while (content.firstChild) content.removeChild(content.firstChild);
    fillAudio(h, content);
  }));
  // Single on/off toggle for the bundled radio playlist.
  content.appendChild(mutable(toggle('radio on/off', h.settings.radio.enabled, (v) => {
    h.settings.radio.enabled = v;
    h.saveSettings();
    h.onMusicVariantChange();
  })));

  // ── SFX bank selector ───────────────────────────────────────────
  // Curated preset packs that retune the entire SFX catalogue en masse
  // (muted / bright / retro / bold / smooth). User per-sfx tweaks below
  // layer on top of the active bank; 'reset this sound' clears the
  // user tweak and falls back to the bank's value for that sound.
  const bankDescNode = el('div', { class: 'help', style: 'margin-bottom: 8px' }, [
    SFX_BANKS.find((b) => b.id === (h.settings.audio.sfxBank ?? 'default'))?.description ?? '',
  ]);
  content.appendChild(select(
    'sound effects preset',
    SFX_BANKS.map((b) => ({ value: b.id, label: b.label })),
    h.settings.audio.sfxBank ?? 'default',
    (v) => {
      h.settings.audio.sfxBank = v;
      h.saveSettings();
      h.onAudioChange();
      bankDescNode.textContent = SFX_BANKS.find((b) => b.id === v)?.description ?? '';
      // Quick preview so the user hears the new character immediately.
      h.testSfx('lock');
    },
  ));
  content.appendChild(bankDescNode);

  // Per-SFX customization. The user picks which sound to tune from a
  // dropdown, then sliders adjust each parameter for that sound. Values
  // default to 1.0× and are stored sparsely in settings.audio.sfxParams
  // keyed by SFX name, so untouched sounds inherit the built-in patches.
  //
  // The full catalogue of tunable sounds + display labels. Order matters:
  // grouped roughly by gameplay phase so the user scans naturally.
  const SFX_OPTIONS: { value: SfxName; label: string }[] = [
    { value: 'move',           label: 'move' },
    { value: 'softdrop',       label: 'soft drop' },
    { value: 'rotate',         label: 'rotate' },
    { value: 'hold',           label: 'hold' },
    { value: 'harddrop',       label: 'hard drop' },
    { value: 'lock',           label: 'lock' },
    { value: 'clear1',         label: 'single clear' },
    { value: 'clear2',         label: 'double clear' },
    { value: 'clear3',         label: 'triple clear' },
    { value: 'clear4',         label: 'tetris clear' },
    { value: 'tspin',          label: 't-spin' },
    { value: 'pc',             label: 'perfect clear' },
    { value: 'b2b',            label: 'back-to-back' },
    { value: 'combo',          label: 'combo' },
    { value: 'levelup',        label: 'level up' },
    { value: 'topout',         label: 'top out' },
    { value: 'achievement',    label: 'achievement' },
    { value: 'error',          label: 'error / rejected' },
    { value: 'countdown',      label: 'countdown beep' },
    { value: 'go',             label: 'go!' },
    { value: 'purr',           label: 'cat purr' },
    // attack_send / attack_receive hidden from this dropdown while
    // battle is unavailable in the main menu — the patches are still
    // wired in code and will return when versus modes are unlocked.
    { value: 'menu_move',      label: 'menu move' },
    { value: 'menu_select',    label: 'menu select' },
    { value: 'menu_back',      label: 'menu back' },
  ];

  // State held in a small closure so the slider rebuild is self-contained.
  let selectedSfx: SfxName = SFX_OPTIONS[0]!.value;
  const sfxControls = el('div', { class: 'sfx-tune-controls' });

  const paramOf = (key: keyof SfxParams): number => {
    const p = h.settings.audio.sfxParams?.[selectedSfx];
    return (p && p[key] != null) ? (p[key] as number) : 1;
  };
  const setParam = (key: keyof SfxParams, v: number) => {
    if (!h.settings.audio.sfxParams) h.settings.audio.sfxParams = {};
    const existing = h.settings.audio.sfxParams[selectedSfx] ?? {};
    h.settings.audio.sfxParams[selectedSfx] = { ...existing, [key]: v };
    h.saveSettings();
    h.onAudioChange();
  };

  // Group label helper — small uppercase divider above each slider band.
  const groupLabel = (text: string): HTMLElement =>
    el('div', { class: 'sfx-tune-group' }, [text]);

  const renderControls = () => {
    while (sfxControls.firstChild) sfxControls.removeChild(sfxControls.firstChild);

    // ── Tone group: how the sound is shaped before mixing ──────────
    sfxControls.appendChild(groupLabel('tone'));
    sfxControls.appendChild(slider({
      label: 'pitch',
      min: 0.6, max: 1.6, step: 0.01,
      value: paramOf('pitch'),
      format: (v) => v === 1 ? 'default' : `${v.toFixed(2)}×`,
      onChange: (v) => setParam('pitch', v),
    }, 'shifts the entire pitch of this sound up or down.'));
    sfxControls.appendChild(slider({
      label: 'brightness',
      min: 0.3, max: 2.0, step: 0.01,
      value: paramOf('brightness'),
      format: (v) => v === 1 ? 'default' : `${v.toFixed(2)}×`,
      onChange: (v) => setParam('brightness', v),
    }, 'opens or closes the filter — higher = more bite, lower = warmer.'));
    sfxControls.appendChild(slider({
      label: 'attack',
      min: 0.3, max: 3.0, step: 0.01,
      value: paramOf('attack'),
      format: (v) => v === 1 ? 'default' : `${v.toFixed(2)}×`,
      onChange: (v) => setParam('attack', v),
    }, 'how sharp the onset is. lower = clicky / aggressive, higher = soft / pillowy.'));
    sfxControls.appendChild(slider({
      label: 'decay',
      min: 0.4, max: 2.0, step: 0.01,
      value: paramOf('decay'),
      format: (v) => v === 1 ? 'default' : `${v.toFixed(2)}×`,
      onChange: (v) => setParam('decay', v),
    }, 'stretches or shortens the tail of the sound.'));

    // ── Mix group: volume + reverb. Pan + crush sliders were removed —
    //    stereo placement is now uniform (bus-level Haas widener) and
    //    lo-fi grit lives inside the patches themselves. ─────────────
    sfxControls.appendChild(groupLabel('mix'));
    sfxControls.appendChild(slider({
      label: 'volume',
      min: 0, max: 2.0, step: 0.01,
      value: paramOf('volume'),
      format: (v) => v === 0 ? 'mute' : v === 1 ? 'default' : `${(v * 100).toFixed(0)}%`,
      onChange: (v) => setParam('volume', v),
    }, 'this sound\'s loudness, on top of the master sfx slider. 0 = mute.'));
    sfxControls.appendChild(slider({
      label: 'reverb',
      min: 0, max: 2.5, step: 0.01,
      value: paramOf('reverb'),
      format: (v) => v === 1 ? 'default' : v === 0 ? 'dry' : `${v.toFixed(2)}×`,
      onChange: (v) => setParam('reverb', v),
    }, 'amount of room reverb sent to the shared wet bus. 0 = completely dry.'));

    // Action row: test current sound, reset to defaults.
    const actions = el('div', { class: 'row', style: 'margin-top: 8px' });
    const testBtn = el('button', { class: 'btn primary' }, ['▶ test this sound']);
    testBtn.addEventListener('click', () => h.testSfx(selectedSfx));
    const resetBtn = el('button', { class: 'btn ghost' }, ['reset this sound']);
    resetBtn.addEventListener('click', () => {
      if (h.settings.audio.sfxParams) delete h.settings.audio.sfxParams[selectedSfx];
      h.saveSettings(); h.onAudioChange();
      renderControls();
    });
    actions.appendChild(testBtn);
    actions.appendChild(resetBtn);
    sfxControls.appendChild(actions);
  };

  // The sfx dropdown + tone/mix tuning live in a collapsible 'advanced'
  // section. The values stored here layer on top of the active bank's
  // overrides — picking a different bank doesn't clear your tweaks,
  // and 'reset this sound' returns to the bank's value (not the
  // built-in default).
  const advanced = el('details', { class: 'audio-advanced' });
  const advancedSummary = el('summary', { class: 'audio-advanced-summary' }, [
    el('span', { class: 'audio-advanced-caret' }, ['▸']),
    'advanced settings',
  ]);
  advanced.appendChild(advancedSummary);
  const advancedBody = el('div', { class: 'audio-advanced-body' });
  advancedBody.appendChild(select('sound effect', SFX_OPTIONS.map((o) => ({ value: o.value, label: o.label })), selectedSfx, (v) => {
    selectedSfx = v as SfxName;
    renderControls();
    h.testSfx(selectedSfx);
  }));
  advancedBody.appendChild(sfxControls);
  advanced.appendChild(advancedBody);
  content.appendChild(advanced);
  renderControls();
  appendTabResetButton(content, h, 'audio');
}

/** Builds an inline CSS string for a palette-card swatch so it visually
 *  previews how a single tetrimino cell would look in the current cell
 *  style. CSS-only approximation of the seven shaders defined in
 *  textures.ts — not pixel-perfect, but close enough to let the player
 *  see what they're picking before committing. */
function swatchCss(color: string, style: string): string {
  const c = color;
  const dark = shiftHex(color, -0.40);
  const darker = shiftHex(color, -0.55);
  const light = shiftHex(color, +0.40);
  switch (style) {
    case 'flat':
      return `background:${c};`;
    case 'jewel':
      return `background:linear-gradient(135deg,${light} 0%,${c} 45%,${dark} 100%);`
           + `box-shadow:inset 1px 1px 0 ${light},inset -1px -1px 0 ${darker},0 0 6px ${c}99;`;
    case 'beveled':
      return `background:${c};box-shadow:inset 0 0 0 2px ${darker};`;
    case 'outline':
      return `background:transparent;box-shadow:inset 0 0 0 2px ${c},0 0 4px ${c};`;
    case 'glass':
      return `background:linear-gradient(180deg,${light} 0%,${c} 55%,${dark} 100%);`
           + `box-shadow:inset 0 1px 0 #ffffff66,0 0 4px ${c}88;`;
    case 'neon':
      return `background:${c}22;box-shadow:inset 0 0 0 1.5px ${c},0 0 8px ${c};`;
    case 'pixel':
      return `background:${c};box-shadow:inset -1px -1px 0 ${darker},inset 1px 1px 0 ${light};`;
    default:
      return `background:${c};box-shadow:0 0 8px ${c};`;
  }
}

/** Tiny hex-channel shifter used by swatchCss. amt > 0 lightens, < 0
 *  darkens. Clamped to 0..255. */
function shiftHex(hex: string, amt: number): string {
  const v = parseInt(hex.replace('#', '').slice(0, 6), 16);
  const r = (v >> 16) & 0xff, g = (v >> 8) & 0xff, b = v & 0xff;
  const adj = (c: number) => Math.max(0, Math.min(255, Math.round(c + amt * 255)));
  return `#${[adj(r), adj(g), adj(b)].map((c) => c.toString(16).padStart(2, '0')).join('')}`;
}

function fillThemes(h: ScreenHost, content: HTMLElement): void {
  // Track every swatch element so we can re-style them in place when the
  // user picks a different cell style further down the tab. Storing the
  // (element, color) pairs avoids re-walking the DOM later.
  const swatchRefs: { el: HTMLElement; color: string }[] = [];

  // Tetrimino style picker — placed BEFORE the palette grid so the
  // dropdown opens over the swatches (which are tall enough that the
  // dropdown otherwise pushed the panel into scroll). Switching also
  // re-applies swatchCss to every preview tile so the grid shows what
  // pieces would look like in the new style.
  content.appendChild(select('tetrimino style', [
    { value: 'jewel',   label: 'jewel' },
    { value: 'flat',    label: 'flat' },
    { value: 'beveled', label: 'beveled' },
    { value: 'outline', label: 'outline' },
    { value: 'glass',   label: 'glass' },
    { value: 'neon',    label: 'neon' },
    { value: 'pixel',   label: 'pixel' },
  ], h.settings.render.rgbCellStyle, (v) => {
    h.settings.render.rgbCellStyle = v as typeof h.settings.render.rgbCellStyle;
    h.saveSettings();
    h.onVideoChange();
    for (const ref of swatchRefs) ref.el.setAttribute('style', swatchCss(ref.color, v));
  }));

  content.appendChild(el('div', { class: 'themes-section-label' }, ['color palette']));
  const grid = el('div', { class: 'palette-grid' });
  const allPalettes = [...PALETTES, ...(h.settings.customPalettes ?? [])];
  allPalettes.forEach((p) => {
    const card = el('div', { class: `palette-card${p.id === h.settings.paletteId ? ' active' : ''}` });
    card.appendChild(el('div', { class: 'name' }, [p.name.toLowerCase()]));
    const sw = el('div', { class: 'palette-swatches' });
    for (const t of ['I', 'O', 'T', 'S', 'Z', 'J', 'L'] as const) {
      const swatch = el('div', { style: swatchCss(p.pieces[t], h.settings.render.rgbCellStyle) });
      swatchRefs.push({ el: swatch, color: p.pieces[t] });
      sw.appendChild(swatch);
    }
    card.appendChild(sw);
    card.addEventListener('click', () => {
      h.playMenuSfx('select');
      h.settings.paletteId = p.id;
      h.saveSettings();
      h.onPaletteChange();
      grid.querySelectorAll('.palette-card').forEach((c) => c.classList.remove('active'));
      card.classList.add('active');
    });
    grid.appendChild(card);
  });
  // Final "custom" tile — opens the custom palette manager.
  const customCount = (h.settings.customPalettes ?? []).length;
  const customCard = el('div', { class: 'palette-card palette-card-custom' });
  customCard.appendChild(el('div', { class: 'name' }, ['custom']));
  customCard.appendChild(el('div', { class: 'palette-card-custom-icon' }, ['+']));
  customCard.appendChild(el('div', { class: 'palette-card-custom-note' }, [
    customCount === 0 ? 'create your own' : `${customCount} saved`,
  ]));
  customCard.addEventListener('click', () => {
    h.playMenuSfx('select');
    h.navigate({ type: 'custom-palettes' });
  });
  grid.appendChild(customCard);
  content.appendChild(grid);
  appendTabResetButton(content, h, 'themes');
}

function fillHandling(h: ScreenHost, content: HTMLElement): void {
  content.appendChild(slider({
    label: 'DAS', min: 0, max: 300, step: 1, value: h.settings.handling.dasMs,
    format: (v) => `${v} ms`,
    onChange: (v) => { h.settings.handling.dasMs = v; h.saveSettings(); h.onHandlingChange(); },
  }));
  content.appendChild(el('div', { class: 'help', style: 'margin-top: -4px; margin-bottom: 10px' }, [
    'delayed auto-shift — how long you must hold a direction before the piece begins to auto-move.',
  ]));
  content.appendChild(slider({
    label: 'ARR', min: 0, max: 100, step: 1, value: h.settings.handling.arrMs,
    format: (v) => v === 0 ? 'instant' : `${v} ms`,
    onChange: (v) => { h.settings.handling.arrMs = v; h.saveSettings(); h.onHandlingChange(); },
  }));
  content.appendChild(el('div', { class: 'help', style: 'margin-top: -4px; margin-bottom: 10px' }, [
    'auto-repeat rate — the interval between each repeated auto-shift after DAS triggers.',
  ]));
  content.appendChild(slider({
    label: 'DCD', min: 0, max: 100, step: 1, value: h.settings.handling.dcdMs,
    format: (v) => `${v} ms`,
    onChange: (v) => { h.settings.handling.dcdMs = v; h.saveSettings(); h.onHandlingChange(); },
  }));
  content.appendChild(el('div', { class: 'help', style: 'margin-top: -4px; margin-bottom: 10px' }, [
    'direction change delay — brief grace period when you reverse direction mid-auto-shift.',
  ]));
  content.appendChild(slider({
    label: 'SDF', min: 1, max: 80, step: 1, value: Math.min(80, isFinite(h.settings.handling.softDropFactor) ? h.settings.handling.softDropFactor : 80),
    format: (v) => v >= 80 ? 'instant' : `${v}×`,
    onChange: (v) => {
      h.settings.handling.softDropFactor = v >= 80 ? Number.POSITIVE_INFINITY : v;
      h.saveSettings(); h.onHandlingChange();
    },
  }));
  content.appendChild(el('div', { class: 'help', style: 'margin-top: -4px;' }, [
    'soft drop factor — how many times faster the piece falls while you hold soft drop.',
  ]));
  content.appendChild(slider({
    label: 'hard drop guard', min: 0, max: 200, step: 10, value: h.settings.handling.hardDropGuardMs,
    format: (v) => v === 0 ? 'off' : `${v} ms`,
    onChange: (v) => { h.settings.handling.hardDropGuardMs = v; h.saveSettings(); h.onHandlingChange(); },
  }));
  content.appendChild(el('div', { class: 'help', style: 'margin-top: -4px;' }, [
    'window after each new piece spawns during which hard drop input is ignored. raise this if you sometimes slam pieces by accident.',
  ]));
  appendTabResetButton(content, h, 'handling');
}

function fillControls(h: ScreenHost, content: HTMLElement): void {
  const actions: { action: Action; label: string }[] = [
    { action: 'left',      label: 'move left' },
    { action: 'right',     label: 'move right' },
    { action: 'softDrop',  label: 'soft drop' },
    { action: 'hardDrop',  label: 'hard drop' },
    { action: 'rotateCW',  label: 'rotate clockwise' },
    { action: 'rotateCCW', label: 'rotate counter-clockwise' },
    { action: 'rotate180', label: 'rotate 180°' },
    { action: 'hold',      label: 'hold piece' },
    { action: 'pause',     label: 'pause' },
    { action: 'restart',   label: 'restart run' },
  ];
  for (const a of actions) {
    const row = el('div', { class: 'keybind-row' });
    row.appendChild(el('div', {}, [a.label.toLowerCase()]));
    const keysEl = el('div', { class: 'keybind-keys' });
    const codes = h.settings.keymap[a.action] ?? [];
    for (let i = 0; i < Math.max(2, codes.length); i++) {
      const code = codes[i];
      const btn = el('button', { class: 'keybind-btn', dataset: { action: a.action, slot: String(i) } }, [code ? keyLabel(code) : '—']);
      btn.addEventListener('click', () => h.beginRebind(a.action, i));
      keysEl.appendChild(btn);
    }
    row.appendChild(keysEl);
    const clearBtn = el('button', { class: 'btn ghost' }, ['clear']);
    clearBtn.addEventListener('click', () => {
      h.settings.keymap[a.action] = [];
      h.saveSettings();
      h.navigate({ type: 'settings', tab: 'controls' });
    });
    row.appendChild(clearBtn);
    content.appendChild(row);
  }
  content.appendChild(el('div', { class: 'help' }, [
    'click a key slot, then press the key you want to assign. press escape to cancel a rebind. two slots per action allow primary + alternate bindings.',
  ]));
  appendTabResetButton(content, h, 'controls');
}

export function buildStats(h: ScreenHost): void {
  clearScreen(h.host);
  const panel = el("div", { class: "panel panel-wide panel-fixed" }); decoratePanel(panel, `settle // ${APP_VERSION}`);
  panel.appendChild(brandHeader(h)); panel.appendChild(screenTitle('stats'));
  // Scrollable body — the panel itself is fixed (.panel-fixed), so all
  // sections live inside .tab-content and fade out at the bottom edge.
  const statsScroll = el('div', { class: 'tab-content section' });

  // Collapsible section helper — every stats group renders as a header
  // that toggles a body block. The first section opens by default so the
  // screen never appears empty on first visit.
  const collapsibles: HTMLElement[] = [];
  const section = (title: string, summary: string, openByDefault: boolean): HTMLElement => {
    const open = openByDefault;
    const head = el('button', {
      type: 'button',
      class: `stats-section-head${open ? ' open' : ''}`,
      'aria-expanded': String(open),
    }, [
      el('span', { class: 'stats-section-caret' }, ['▸']),
      el('span', { class: 'stats-section-title' }, [title]),
      el('span', { class: 'stats-section-summary' }, [summary]),
    ]);
    const body = el('div', { class: `stats-section-body${open ? ' open' : ''}` });
    head.addEventListener('click', () => {
      const wasOpen = head.classList.contains('open');
      head.classList.toggle('open');
      body.classList.toggle('open');
      head.setAttribute('aria-expanded', String(!wasOpen));
      h.playMenuSfx('move');
    });
    statsScroll.appendChild(head);
    statsScroll.appendChild(body);
    collapsibles.push(head, body);
    return body;
  };

  // ── Personal bests ─────────────────────────────────────────────────
  const pbCount = ALL_MODES.filter((m) => m.pbKey && h.stats.pbs[m.pbKey!] != null).length;
  const pbTotal = ALL_MODES.filter((m) => m.pbKey).length;
  const pbBody = section('personal bests', `${pbCount} / ${pbTotal} set`, true);
  for (const m of ALL_MODES) {
    if (!m.pbKey) continue;
    const pbRaw = h.stats.pbs[m.pbKey];
    const value = pbRaw == null ? '—' : (m.pbLowerBetter ? formatTime(pbRaw) : pbRaw.toLocaleString());
    pbBody.appendChild(statRow(m.name, value, pbRaw != null));
  }

  // ── Lifetime totals ────────────────────────────────────────────────
  const totalsBody = section('lifetime totals', formatTime(h.stats.totalPlaytimeMs), false);
  totalsBody.appendChild(statRow('runs completed',  `${h.stats.totalRuns.toLocaleString()}`));
  totalsBody.appendChild(statRow('lines cleared',   `${h.stats.totalLines.toLocaleString()}`));
  totalsBody.appendChild(statRow('pieces placed',   `${h.stats.totalPieces.toLocaleString()}`));
  totalsBody.appendChild(statRow('hard drops',      `${h.stats.totalHardDrops.toLocaleString()}`));
  totalsBody.appendChild(statRow('tetrises',        `${h.stats.totalTetrises.toLocaleString()}`));
  totalsBody.appendChild(statRow('perfect clears',  `${h.stats.totalPerfectClears.toLocaleString()}`));
  totalsBody.appendChild(statRow('total playtime',  formatTime(h.stats.totalPlaytimeMs)));

  // ── T-spin breakdown ───────────────────────────────────────────────
  const tspinBody = section('t-spin breakdown', `${h.stats.totalTspins.toLocaleString()} total`, false);
  tspinBody.appendChild(statRow('full t-spins',  `${h.stats.totalTspinsFull.toLocaleString()}`));
  tspinBody.appendChild(statRow('mini t-spins',  `${h.stats.totalTspinsMini.toLocaleString()}`));
  tspinBody.appendChild(statRow('singles',       `${h.stats.totalTspinSingles.toLocaleString()}`));
  tspinBody.appendChild(statRow('doubles',       `${h.stats.totalTspinDoubles.toLocaleString()}`));
  tspinBody.appendChild(statRow('triples',       `${h.stats.totalTspinTriples.toLocaleString()}`));

  // Achievements section uses the same collapsible head but contains the
  // grouped grid below. Tracked separately because it has its own layout.
  const unlocked = new Set(h.stats.achievements);
  const achBody = section('achievements', `${unlocked.size} / ${ACHIEVEMENTS.length}`, false);
  // Build a stand-in progress ctx so locked count-gated achievements can
  // display "current / target" progress bars. We don't have a live engine
  // in the stats screen — only the persisted totals.
  const progressCtx = {
    totals: {
      lines: h.stats.totalLines,
      pieces: h.stats.totalPieces,
      tetrises: h.stats.totalTetrises,
      tspins: h.stats.totalTspins,
      tspinsFull: h.stats.totalTspinsFull,
      tspinsMini: h.stats.totalTspinsMini,
      tspinSingles: h.stats.totalTspinSingles,
      tspinDoubles: h.stats.totalTspinDoubles,
      tspinTriples: h.stats.totalTspinTriples,
      perfectClears: h.stats.totalPerfectClears,
      hardDrops: h.stats.totalHardDrops,
      runs: h.stats.totalRuns,
      playtimeMs: h.stats.totalPlaytimeMs,
      modeRuns: h.stats.modeRuns,
    },
    // Below fields are not used by progress(), but the AchievementCtx
    // type requires them. We pass minimal stubs.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    engine: null as any,
    modeId: 'marathon' as ModeId,
    lastClear: null,
    bestComboThisRun: 0,
    bestB2BThisRun: 0,
  };
  // Each category is also its own click-to-expand band so the user can
  // collapse a category they've already cleared. All four start
  // collapsed inside the (already-collapsed-by-default) Achievements
  // body — open them progressively as you explore.
  for (const cat of CATEGORIES) {
    const inCat = ACHIEVEMENTS.filter((a) => a.category === cat.id);
    if (inCat.length === 0) continue;
    const unlockedInCat = inCat.filter((a) => unlocked.has(a.id)).length;
    const catHead = el('button', {
      type: 'button',
      class: 'ach-category-header',
      'aria-expanded': 'false',
    }, [
      el('span', { class: 'ach-category-caret' }, ['▸']),
      el('div', { class: 'ach-category-glyph' }, [icon(cat.icon)]),
      el('div', { class: 'ach-category-meta' }, [
        el('div', { class: 'ach-category-label' }, [cat.label]),
        el('div', { class: 'ach-category-tag' }, [cat.tagline]),
      ]),
      el('div', { class: 'ach-category-count' }, [`${unlockedInCat} / ${inCat.length}`]),
    ]);
    const grid = el('div', { class: 'achievements-grid ach-category-body' });
    catHead.addEventListener('click', () => {
      const isOpen = catHead.classList.toggle('open');
      grid.classList.toggle('open', isOpen);
      catHead.setAttribute('aria-expanded', String(isOpen));
      h.playMenuSfx('move');
    });
    for (const a of inCat) {
      const isUnlocked = unlocked.has(a.id);
      // Progress bar — only show for count-gated locked achievements that
      // expose a progress function. Unlocked cards don't need a bar.
      let progressEl: HTMLElement | null = null;
      if (!isUnlocked && a.progress) {
        const p = a.progress(progressCtx);
        if (p && p.target > 0) {
          const pct = Math.min(100, Math.max(0, (p.current / p.target) * 100));
          const fmt = (n: number) => n >= 1000 ? n.toLocaleString() : `${n}`;
          const unit = p.unit ?? '';
          progressEl = el('div', { class: 'ach-progress' }, [
            el('div', { class: 'ach-progress-bar' }, [
              el('div', { class: 'ach-progress-fill', style: `width: ${pct.toFixed(1)}%` }),
            ]),
            el('div', { class: 'ach-progress-text' }, [`${fmt(p.current)}${unit} / ${fmt(p.target)}${unit}`]),
          ]);
        }
      }
      grid.appendChild(el('div', { class: `ach-card${isUnlocked ? '' : ' locked'} ach-cat-${a.category}` }, [
        el('div', { class: 'ach-card-icon' }, [
          isUnlocked ? icon(cat.icon) : icon('lock-alt-solid'),
        ]),
        el('div', { class: 'ach-card-body' }, [
          el('div', { class: 'ach-card-name' }, [a.name]),
          el('div', { class: 'ach-card-desc' }, [a.description]),
          progressEl,
        ]),
      ]));
    }
    achBody.appendChild(catHead);
    achBody.appendChild(grid);
  }
  void collapsibles;
  panel.appendChild(statsScroll);
  panel.appendChild(el('div', { class: 'row' }, [
    backButton(h, () => h.navigate({ type: 'menu' })),
    el('button', { class: 'btn ghost', onclick: async () => {
      const ok = await confirmModal({
        title: 'clear all stats?',
        body: 'this wipes every personal best, lifetime total, and unlocked achievement on this profile. there is no undo.',
        confirmLabel: 'clear all stats',
      });
      if (!ok) return;
      h.stats.pbs = {};
      h.stats.totalRuns = 0;
      h.stats.totalLines = 0;
      h.stats.totalPieces = 0;
      h.stats.totalTetrises = 0;
      h.stats.totalTspins = 0;
      h.stats.totalTspinsFull = 0;
      h.stats.totalTspinsMini = 0;
      h.stats.totalTspinSingles = 0;
      h.stats.totalTspinDoubles = 0;
      h.stats.totalTspinTriples = 0;
      h.stats.totalPerfectClears = 0;
      h.stats.totalHardDrops = 0;
      h.stats.totalPlaytimeMs = 0;
      h.stats.modeRuns = {};
      h.stats.achievements = [];
      h.saveStats();
      h.navigate({ type: 'stats' });
    } }, ['clear all stats']),
  ]));
  h.host.appendChild(panel);
  const onKeyStats = (e: KeyboardEvent) => {
    if (e.code === 'Escape') { e.preventDefault(); h.playMenuSfx('back'); h.navigate({ type: 'menu' }); }
  };
  window.addEventListener('keydown', onKeyStats);
  registerScreenCleanup(() => window.removeEventListener('keydown', onKeyStats));
}

export function buildAbout(h: ScreenHost): void {
  clearScreen(h.host);
  const panel = el('div', { class: 'panel panel-wide panel-fixed' });
  decoratePanel(panel, `settle // ${APP_VERSION}`);
  panel.appendChild(brandHeader(h)); panel.appendChild(screenTitle('help'));

  // Tabbed help — keeps the panel compact and replaces the previous wall
  // of prose. Each tab focuses on one concept.
  const tabs = el('div', { class: 'tabs' });
  // 'battle' tab intentionally hidden while battle is disabled from the
  // main menu — fillHelpBattle is preserved in source for when it returns.
  const TABS = [
    { id: 'play',     label: 'how to play' },
    { id: 'advanced', label: 'advanced' },
    { id: 'tips',     label: 'strategy' },
    { id: 'glossary', label: 'glossary' },
  ] as const;
  type HelpTab = typeof TABS[number]['id'];
  let activeTab: HelpTab = 'play';
  const content = el('div', { class: 'tab-content section' });

  const renderTab = () => {
    while (content.firstChild) content.removeChild(content.firstChild);
    if (activeTab === 'play') fillHelpPlay(content);
    else if (activeTab === 'advanced') fillHelpAdvanced(content);
    else if (activeTab === 'tips') fillHelpTips(content);
    else if (activeTab === 'glossary') fillHelpGlossary(content);
  };

  TABS.forEach((t) => {
    const b = el('button', { class: `tab${t.id === activeTab ? ' active' : ''}` }, [t.label]);
    b.addEventListener('click', () => {
      if (activeTab === t.id) return;
      h.playMenuSfx('move');
      activeTab = t.id;
      tabs.querySelectorAll('.tab').forEach((x) => x.classList.remove('active'));
      b.classList.add('active');
      renderTab();
    });
    tabs.appendChild(b);
  });
  // Keyboard tab cycling — left/right arrows + tab key step through the
  // tab list, mirroring the settings panel.
  const helpKey = (e: KeyboardEvent) => {
    if (e.code === 'ArrowLeft' || (e.code === 'Tab' && e.shiftKey)) {
      e.preventDefault();
      const i = TABS.findIndex((t) => t.id === activeTab);
      const next = TABS[(i - 1 + TABS.length) % TABS.length]!.id;
      if (next === activeTab) return;
      h.playMenuSfx('move');
      activeTab = next;
      tabs.querySelectorAll('.tab').forEach((b, idx) => b.classList.toggle('active', TABS[idx]?.id === next));
      renderTab();
    } else if (e.code === 'ArrowRight' || e.code === 'Tab') {
      e.preventDefault();
      const i = TABS.findIndex((t) => t.id === activeTab);
      const next = TABS[(i + 1) % TABS.length]!.id;
      if (next === activeTab) return;
      h.playMenuSfx('move');
      activeTab = next;
      tabs.querySelectorAll('.tab').forEach((b, idx) => b.classList.toggle('active', TABS[idx]?.id === next));
      renderTab();
    } else if (e.code === 'Escape') {
      e.preventDefault();
      h.playMenuSfx('back');
      h.navigate({ type: 'menu' });
    }
  };
  window.addEventListener('keydown', helpKey);
  registerScreenCleanup(() => window.removeEventListener('keydown', helpKey));
  panel.appendChild(tabs);
  panel.appendChild(content);
  renderTab();

  panel.appendChild(el('div', { class: 'row' }, [
    backButton(h, () => h.navigate({ type: 'menu' })),
  ]));
  h.host.appendChild(panel);
}

// ─── Help tab fillers ─────────────────────────────────────────────────

function helpSection(title: string, body: string | HTMLElement): HTMLElement {
  const wrap = el('div', { class: 'help-section' });
  wrap.appendChild(el('div', { class: 'help-section-title' }, [title]));
  if (typeof body === 'string') {
    wrap.appendChild(el('div', { class: 'help-section-body' }, [body]));
  } else {
    wrap.appendChild(body);
  }
  return wrap;
}

function helpList(items: { term: string; def: string }[]): HTMLElement {
  const list = el('div', { class: 'help-deflist' });
  for (const it of items) {
    list.appendChild(el('div', { class: 'help-defrow' }, [
      el('div', { class: 'help-defterm' }, [it.term]),
      el('div', { class: 'help-defdef' }, [it.def]),
    ]));
  }
  return list;
}

// ─── Beginner tab ─────────────────────────────────────────────────────
function fillHelpPlay(content: HTMLElement): void {
  content.appendChild(helpSection('objective',
    'seven different shapes fall from the top of the playfield, one at a time. move and rotate each shape as it falls to build complete horizontal rows. a row that fills with no gaps clears off the board and any pieces above it drop down. the match ends when a new shape can no longer enter the playfield.'));
  content.appendChild(helpSection('default controls', helpList([
    { term: '← / →',     def: 'move the current piece one column left or right.' },
    { term: '↓',         def: 'soft drop. hold to accelerate the piece downward.' },
    { term: 'space',     def: 'hard drop. instantly drops the piece to its landing position and locks it.' },
    { term: '↑ or x',    def: 'rotate the piece clockwise.' },
    { term: 'z',         def: 'rotate the piece counter-clockwise.' },
    { term: 'a / shift', def: 'rotate 180 degrees.' },
    { term: 'c',         def: 'place the current piece into the hold slot for later use.' },
    { term: 'esc',       def: 'open the pause menu. hold to abandon the current run.' },
    { term: 'r',         def: 'restart the current mode from the beginning.' },
  ])));
  content.appendChild(helpSection('reading the playfield',
    'the translucent silhouette beneath the active piece is the ghost piece — it marks where the piece will land if hard-dropped. the column on the right is the queue, showing the next five shapes in order. the slot on the left is hold; pressing the hold key swaps your current piece with whatever is stored there.'));
  content.appendChild(helpSection('locking and lock delay',
    'a piece locks into the stack the moment it touches the ground and stops moving. a brief window of around half a second — called lock delay — lets you keep adjusting the piece after it touches down. every move or rotation refreshes that window, up to a limit of fifteen refreshes per piece.'));
  content.appendChild(el('div', { class: 'help', style: 'margin-top: 12px' }, [
    'every key shown above is rebindable from settings → controls. two keys can share the same action.',
  ]));
}

// ─── Advanced tab ────────────────────────────────────────────────────
function fillHelpAdvanced(content: HTMLElement): void {
  content.appendChild(helpSection('the seven pieces',
    'each shape is called a tetrimino. the seven are named after the letters they resemble: i, o, t, s, z, j, l. settle uses a 7-bag randomizer — pieces are dealt from a shuffled set of all seven, so the gap between any two appearances of the same piece is never more than twelve placements.'));
  content.appendChild(helpSection('rotation system (srs)',
    'settle implements the guideline super rotation system. when a rotation would overlap a wall, the floor, or part of the stack, the piece is automatically nudged through a sequence of test positions called wall kicks. if every test fails the rotation is rejected. wall kicks are what make j-, l-, s-, z-, and t-piece spins into tight slots possible.'));
  content.appendChild(helpSection('t-spins',
    'a t-spin is a special clear performed by rotating a t-piece into a slot where at least three of the four cells diagonally adjacent to the t\'s center are blocked. the last action before locking must have been a rotation, not a translation. a full t-spin requires both front corners (on the open side) to be filled, or that the rotation succeeded only on the deepest wall kick (the tst kick). anything less is classified as a mini t-spin and scores reduced bonuses.'));
  content.appendChild(helpSection('back-to-back (b2b)',
    'a clear is "difficult" if it is a tetris (four lines) or any t-spin that cleared at least one line. back-to-back activates when two difficult clears occur in a row with no intervening normal clear. while b2b is active, every difficult clear awards a 1.5× score multiplier and increments the b2b chain counter. a single, double, or triple that is not a t-spin will break the chain.'));
  content.appendChild(helpSection('combo',
    'every line clear is part of a combo as long as the next placement also clears at least one line. the combo counter resets the moment a piece locks without clearing anything. each link in a combo grants additional score.'));
  content.appendChild(helpSection('perfect clear',
    'a perfect clear occurs when a line clear leaves the playfield completely empty. perfect clears award the largest score bonus in the game.'));
  content.appendChild(helpSection('hold',
    'the hold key stores the active piece in a reserve slot. if the slot already contains a piece, the two swap and the swapped-in piece becomes active. hold cannot be used twice in a row — once you swap, the slot locks until the new active piece is placed.'));
}

// ─── Battle tab ──────────────────────────────────────────────────────
function fillHelpBattle(content: HTMLElement): void {
  content.appendChild(helpSection('match flow',
    'battle places you and one to three ai opponents on adjacent playfields. everyone plays simultaneously. clearing lines queues garbage against the other side. the match ends the moment the player or every remaining ai tops out, depending on the mode.'));
  content.appendChild(helpSection('the modes', 'pick one before each match from the battle screen.'));
  content.appendChild(helpList([
    { term: 'duel',          def: '1v1. last to top out wins. the standard battle.' },
    { term: 'triple threat', def: '1v2. your outgoing garbage splits across both ais (the one with the smaller incoming queue takes the remainder). either ai topping out is one down — survive the swarm.' },
    { term: 'royal rumble',  def: '1v3 free-for-all. every player for themselves — each ai splits its outgoing garbage between you and the other living ais, so topping one out genuinely lightens your load. last alive wins.' },
    { term: 'garbage brawl', def: '1v1 with eight garbage rows pre-filled on both sides. tests downstacking under pressure.' },
    { term: 'sudden death',  def: '1v1 with every attack value multiplied by 1.5×. lethal exchanges; tiny mistakes snowball.' },
    { term: 'sprint versus', def: '1v1 race to 40 lines. no garbage is exchanged — fastest to forty wins. tests raw pps.' },
  ]));
  content.appendChild(helpSection('damage table',
    'the base attack value of every clear type, in lines of garbage sent. sudden-death multiplies all values by 1.5×, rounded up.'));
  content.appendChild(helpList([
    { term: 'single',           def: '0 lines.' },
    { term: 'double',           def: '1 line.' },
    { term: 'triple',           def: '2 lines.' },
    { term: 'tetris',           def: '4 lines.' },
    { term: 't-spin single',    def: '2 lines.' },
    { term: 't-spin double',    def: '4 lines.' },
    { term: 't-spin triple',    def: '6 lines.' },
    { term: 'back-to-back',     def: '+1 line on every difficult clear in the chain.' },
    { term: 'perfect clear',    def: '+10 lines on top of the clear\'s base damage.' },
    { term: 'combo',            def: '+1 line per two combo levels, capped at +5.' },
  ]));
  content.appendChild(helpSection('cancellation',
    'outgoing attacks first cancel against incoming damage in your own pending queue. the "incoming" indicator beside your playfield shows how many garbage lines are pending against you. clearing lines fast enough to drain the indicator to zero before your next piece spawns will fully neutralize the attack.'));
  content.appendChild(helpSection('garbage delivery',
    'queued garbage is injected into the bottom of your playfield the moment your next piece spawns. each garbage row contains a single hole — the hole position is randomized once per delivery, so a long line of incoming garbage all shares the same hole column and can be cleared by dropping an i-piece into it.'));
  content.appendChild(helpSection('attack cues',
    'when you send an attack, a glowing projectile streaks from your playfield toward the opponent and an ascending whoosh plays. when you receive garbage, the rim of your playfield flashes red and a low thump plays. use the cues to anticipate when damage will land — you can usually fit one more piece before it arrives.'));
}

// ─── Strategy tab ────────────────────────────────────────────────────
function fillHelpTips(content: HTMLElement): void {
  content.appendChild(helpSection('keep the stack flat',
    'a flat surface accepts any piece. a jagged surface forces you to waste pieces filling small gaps and limits the rotations you can perform. place pieces such that adjacent columns differ in height by no more than two rows whenever possible.'));
  content.appendChild(helpSection('reserve the rightmost column',
    'leave column 10 empty and four rows deep. when an i-piece arrives, drop it vertically into that well to clear four rows at once. if the i-piece arrives before the well is ready, place it in hold and use the next piece instead.'));
  content.appendChild(helpSection('plan with the queue',
    'the next-piece queue shows five pieces in advance. before placing the current piece, look ahead two or three pieces and choose a placement that leaves a useful surface for the upcoming shapes. the hold slot is a sixth lookahead — use it.'));
  content.appendChild(helpSection('garbage mode: find the column, dig down',
    'each garbage row has exactly one hole. the holes are arranged in a single column at the start of the run. identify that column, leave it open, and place pieces around it to clear rows from the top down. burying the hole forces you to clear extra rows later.'));
  content.appendChild(helpSection('master mode: prioritize hard drop',
    'at level 15 and above, gravity is fast enough that soft drops barely save time. move, rotate, and hard drop — each piece should be in the air for less than a second. if you find yourself misplacing pieces at lock delay, lower your das and arr values in settings → handling.'));
  content.appendChild(helpSection('sprint: minimize hard drops',
    'sprint times are determined by piece-placement speed. the fastest sprint runs use no hard drops — soft-drop assistance is faster once you can land pieces precisely. aim for two pieces per second as a comfortable target.'));
}

// ─── Glossary tab ────────────────────────────────────────────────────
function fillHelpGlossary(content: HTMLElement): void {
  content.appendChild(helpList([
    { term: 'tetrimino',  def: 'a four-cell shape. tetris uses seven of them: i, o, t, s, z, j, l.' },
    { term: 'playfield',  def: 'the 10-column, 20-row playing area where pieces fall and lock.' },
    { term: 'spawn area', def: 'the hidden buffer above the visible playfield where new pieces appear before becoming visible.' },
    { term: 'stack',      def: 'the accumulating mass of locked pieces at the bottom of the playfield.' },
    { term: 'ghost piece',def: 'the translucent silhouette showing where the active piece will land.' },
    { term: 'queue',      def: 'the list of upcoming pieces shown beside the playfield.' },
    { term: 'hold',       def: 'a single-piece reserve slot. press the hold key to swap the active piece into it.' },
    { term: 'srs',        def: 'super rotation system. the standard rotation and wall-kick rules used in guideline tetris.' },
    { term: 'wall kick',  def: 'an automatic position adjustment when a rotation would overlap a wall, floor, or stack cell.' },
    { term: '7-bag',      def: 'the randomizer that deals every seven pieces as a shuffled set of all seven shapes.' },
    { term: 'lock delay', def: 'the brief window before a touched-down piece is committed to the stack. resets on move or rotate, up to fifteen times.' },
    { term: 'irs / ihs',  def: 'initial rotation state and initial hold state. holding rotate or hold while a new piece spawns applies it immediately.' },
    { term: 'das',        def: 'delayed auto-shift. the pause before a held direction begins auto-moving the piece.' },
    { term: 'arr',        def: 'auto-repeat rate. the interval between auto-shifts after das triggers.' },
    { term: 'sdf',        def: 'soft drop factor. the multiplier applied to gravity while soft drop is held.' },
    { term: 'finesse',    def: 'the efficiency of piece placement — using the fewest possible inputs to position each tetrimino.' },
    { term: 'tetris',     def: 'a clear of exactly four lines from a single piece. always an i-piece.' },
    { term: 't-spin',     def: 'a clear in which the last action before locking was a t-piece rotation, with at least three of the t\'s diagonal corners blocked.' },
    { term: 'mini t-spin',def: 'a t-spin where the front corners are not both filled and the rotation did not require the deepest kick. scores less than a full t-spin.' },
    { term: 'tst',        def: 't-spin triple. the deep t-piece kick that allows a triple clear with a t-piece.' },
    { term: 'b2b',        def: 'back-to-back. a chain of difficult clears (tetrises or t-spins) uninterrupted by a normal clear. each link scores 1.5× and sends extra garbage.' },
    { term: 'combo',      def: 'consecutive line clears with no zero-clear locks between them. resets to zero on any placement that clears no lines.' },
    { term: 'perfect clear', def: 'also called pc or all-clear. a clear that leaves the playfield empty. awards a large score bonus and extra garbage.' },
    { term: 'garbage',    def: 'pre-filled rows pushed up from below the stack. each garbage row has a single hole. used in garbage mode.' },
    { term: 'top-out',    def: 'the losing condition: a new piece cannot spawn because the spawn area is blocked.' },
  ]));
}

export function buildVersusSelect(
  h: ScreenHost,
  current: 'easy' | 'medium' | 'hard' | 'nightmare',
  currentMode: 'duel' | 'triple' | 'royal' | 'cheese-brawl' | 'sudden-death' | 'sprint-versus',
  onChoose: (d: 'easy' | 'medium' | 'hard' | 'nightmare', m: typeof currentMode) => void,
): void {
  clearScreen(h.host);
  const panel = el("div", { class: "panel panel-wide" }); decoratePanel(panel, `settle // ${APP_VERSION}`);
  panel.appendChild(brandHeader(h)); panel.appendChild(screenTitle('battle'));

  // Two stacked selectors: mode (rule variant) + difficulty (AI skill).
  // We track both in closure state and only fire onChoose when the
  // player commits with the start button.
  let selectedDifficulty = current;
  let selectedMode = currentMode;

  // ── Mode selector ──────────────────────────────────────────────────
  panel.appendChild(el('div', { class: 'themes-section-label' }, ['modes']));
  const modeList = el('div', { class: 'menu-list', style: 'margin-bottom: 12px' });
  const MODE_ITEMS: { id: typeof currentMode; icon: string; label: string; desc: string; tag: string }[] = [
    { id: 'duel',          icon: 'versus-duel',         label: 'duel',          desc: 'last one standing',                       tag: '1v1' },
    { id: 'triple',        icon: 'users-solid',         label: 'triple threat', desc: 'three player free-for-all against two computers', tag: '1v2' },
    { id: 'royal',         icon: 'crown-solid',         label: 'royal rumble',  desc: 'free-for-all, every player for themselves', tag: '1v3' },
    { id: 'cheese-brawl',  icon: 'trash-alt-solid',     label: 'garbage brawl', desc: 'both sides start with 8 garbage rows',    tag: '1v1' },
    { id: 'sudden-death',  icon: 'bolt',                label: 'sudden death',  desc: 'every attack amplified by 1.5×',          tag: '1v1' },
    { id: 'sprint-versus', icon: 'flag-checkered-solid', label: 'sprint versus', desc: 'race to 40 lines, no garbage',           tag: '1v1' },
  ];
  const renderModeFocus = () => {
    modeList.querySelectorAll('.menu-item').forEach((c, i) => {
      c.classList.toggle('focus', MODE_ITEMS[i]?.id === selectedMode);
    });
  };
  MODE_ITEMS.forEach((m) => {
    const item = el('div', { class: `menu-item${m.id === selectedMode ? ' focus' : ''}` }, [
      el('div', { class: 'menu-icon' }, [icon(m.icon)]),
      el('div', { class: 'menu-body' }, [
        el('div', { class: 'menu-label' }, [m.label]),
        el('div', { class: 'menu-desc' }, [m.desc]),
      ]),
      el('div', { class: 'right' }, [m.tag]),
    ]);
    item.addEventListener('click', () => {
      h.playMenuSfx('move');
      selectedMode = m.id;
      renderModeFocus();
    });
    modeList.appendChild(item);
  });
  panel.appendChild(modeList);

  // ── Difficulty selector ────────────────────────────────────────────
  // Extra top margin so the 'computer skill' label has visible breathing
  // room from the sprint-versus button above (was reading too cramped).
  panel.appendChild(el('div', {
    class: 'themes-section-label',
    style: 'margin-top: 22px',
  }, ['computer skill']));
  const diffList = el('div', { class: 'menu-list', style: 'margin-bottom: 14px' });
  const DIFF_ITEMS: { id: 'easy'|'medium'|'hard'|'nightmare'; icon: string; label: string; desc: string; pps: string }[] = [
    { id: 'easy',      icon: '◦', label: 'easy',      desc: 'casual placement, forgiving tempo',          pps: '0.6 pps' },
    { id: 'medium',    icon: '◇', label: 'medium',    desc: 'solid technique, steady pressure',           pps: '1.3 pps' },
    { id: 'hard',      icon: '◆', label: 'hard',      desc: 'tetris-oriented stacking, fast tempo',       pps: '2.2 pps' },
    { id: 'nightmare', icon: '✦', label: 'nightmare', desc: 'inhuman tempo, zero mistakes',               pps: '3.6 pps' },
  ];
  const renderDiffFocus = () => {
    diffList.querySelectorAll('.menu-item').forEach((c, i) => {
      c.classList.toggle('focus', DIFF_ITEMS[i]?.id === selectedDifficulty);
    });
  };
  DIFF_ITEMS.forEach((it) => {
    const item = el('div', { class: `menu-item${it.id === selectedDifficulty ? ' focus' : ''}` }, [
      el('div', { class: 'menu-icon' }, [it.icon]),
      el('div', { class: 'menu-body' }, [
        el('div', { class: 'menu-label' }, [it.label]),
        el('div', { class: 'menu-desc' }, [it.desc]),
      ]),
      el('div', { class: 'right' }, [it.pps]),
    ]);
    item.addEventListener('click', () => {
      h.playMenuSfx('move');
      selectedDifficulty = it.id;
      renderDiffFocus();
    });
    diffList.appendChild(item);
  });
  panel.appendChild(diffList);

  panel.appendChild(el('div', { class: 'row' }, [
    el('button', { class: 'btn primary', onclick: () => { h.playMenuSfx('select'); onChoose(selectedDifficulty, selectedMode); } }, ['start match']),
    backButton(h, () => h.navigate({ type: 'menu' })),
  ]));
  h.host.appendChild(panel);
}

export function buildPause(
  h: ScreenHost,
  ctx: {
    onResume: () => void;
    onRestart: () => void;
    onSettings: () => void;
    onQuit: () => void;
    /** Display label for what's currently being played — "marathon",
     *  "royal rumble", etc. Shown beneath the panel title. */
    modeLabel?: string;
    stats: { score: number; lines: number; pieces: number; level: number; pps: number; elapsedMs: number };
    pbContext?: { label: string; target: string; status: 'ahead' | 'behind' | 'baseline' | null };
  },
): void {
  clearScreen(h.host);
  const panel = el("div", { class: "panel" }); decoratePanel(panel, `settle // ${APP_VERSION}`);
  panel.appendChild(brandHeader(h)); panel.appendChild(screenTitle('paused'));
  // Combined mode + PB callout. When a PB is being tracked, the line
  // reads "<mode> · personal best to beat: <target> [status]"; for
  // modes without a PB it just shows the mode name. This collapses
  // what used to be two separate elements (mode subtitle + PB chip).
  if (ctx.modeLabel || ctx.pbContext) {
    const statusBadge = ctx.pbContext?.status === 'ahead' ? 'on pace'
                      : ctx.pbContext?.status === 'behind' ? 'behind pace'
                      : null;
    const statusClass = ctx.pbContext?.status === 'ahead' ? ' ahead'
                      : ctx.pbContext?.status === 'behind' ? ' behind' : '';
    const children: (HTMLElement | string | null)[] = [];
    if (ctx.modeLabel) {
      children.push(el('span', { class: 'pb-mode' }, [`${ctx.modeLabel} mode`]));
    }
    if (ctx.pbContext) {
      if (ctx.modeLabel) children.push(el('span', { class: 'pb-sep' }, ['·']));
      children.push(el('span', { class: 'pb-label' }, [ctx.pbContext.label]));
      if (ctx.pbContext.target) {
        children.push(el('span', { class: 'pb-target' }, [ctx.pbContext.target]));
      }
      if (statusBadge) children.push(el('span', { class: 'pb-status' }, [statusBadge]));
    }
    panel.appendChild(el('div', { class: `pause-pb-callout${statusClass}` }, children));
  }
  // Stats summary
  const sg = el('div', { class: 'results-grid' });
  const s = ctx.stats;
  const rows: [string, string][] = [
    ['score', s.score.toLocaleString()],
    ['lines', `${s.lines}`],
    ['level', `${s.level}`],
    ['pieces', `${s.pieces}`],
    ['pps', s.pps.toFixed(2)],
    ['time', formatTime(s.elapsedMs)],
  ];
  for (const [label, value] of rows) {
    sg.appendChild(el('div', { class: 'result-stat' }, [
      el('div', { class: 'label' }, [label]),
      el('div', { class: 'value' }, [value]),
    ]));
  }
  panel.appendChild(sg);
  const list = el('div', { class: 'menu-list' });
  const items: { icon: string; label: string; desc: string; cb: () => void }[] = [
    { icon: 'play',     label: 'resume',       desc: 'continue the current run',             cb: ctx.onResume },
    { icon: 'restart',  label: 'restart',      desc: 'reset and begin from the first piece', cb: ctx.onRestart },
    { icon: 'settings', label: 'settings',     desc: 'adjust controls, video, or audio',     cb: ctx.onSettings },
    { icon: 'back',     label: 'quit to menu', desc: 'end this run and return to menu',      cb: ctx.onQuit },
  ];
  items.forEach((it, i) => {
    const item = el('div', { class: `menu-item${i === 0 ? ' focus' : ''}` }, [
      el('div', { class: 'menu-icon' }, [icon(it.icon)]),
      el('div', { class: 'menu-body' }, [
        el('div', { class: 'menu-label' }, [it.label]),
        el('div', { class: 'menu-desc' }, [it.desc]),
      ]),
    ]);
    item.addEventListener('click', () => { h.playMenuSfx('select'); it.cb(); });
    list.appendChild(item);
  });
  panel.appendChild(list);
  h.host.appendChild(panel);

  // Keyboard handler.
  const onKey = (e: KeyboardEvent) => {
    if (e.code === 'Escape') { e.preventDefault(); ctx.onResume(); return; }
    if (e.code === 'KeyR') { e.preventDefault(); h.playMenuSfx('select'); ctx.onRestart(); return; }
    if (e.code === 'KeyS') { e.preventDefault(); h.playMenuSfx('select'); ctx.onSettings(); return; }
    if (e.code === 'KeyQ') { e.preventDefault(); h.playMenuSfx('back'); ctx.onQuit(); return; }
    if (e.code === 'ArrowDown' || e.code === 'KeyJ') {
      e.preventDefault();
      const items = panel.querySelectorAll<HTMLElement>('.menu-item');
      let i = Array.from(items).findIndex((x) => x.classList.contains('focus'));
      items[i]?.classList.remove('focus');
      i = (i + 1) % items.length;
      items[i]?.classList.add('focus');
      h.playMenuSfx('move');
    } else if (e.code === 'ArrowUp' || e.code === 'KeyK') {
      e.preventDefault();
      const items = panel.querySelectorAll<HTMLElement>('.menu-item');
      let i = Array.from(items).findIndex((x) => x.classList.contains('focus'));
      items[i]?.classList.remove('focus');
      i = (i - 1 + items.length) % items.length;
      items[i]?.classList.add('focus');
      h.playMenuSfx('move');
    } else if (e.code === 'Enter' || e.code === 'Space') {
      e.preventDefault();
      const items = panel.querySelectorAll<HTMLElement>('.menu-item');
      const i = Array.from(items).findIndex((x) => x.classList.contains('focus'));
      h.playMenuSfx('select');
      (items[i] as HTMLElement | undefined)?.click();
    }
  };
  window.addEventListener('keydown', onKey);
  registerScreenCleanup(() => window.removeEventListener('keydown', onKey));

  // Pause is dismissed only via explicit controls — Escape key, the
  // Resume / Restart / Settings / Quit menu items, or the burger
  // button (which calls onResume via main.ts onBurgerClick). The
  // earlier click-outside-the-panel-to-resume behavior was removed
  // because it fired inconsistently across event paths and caused
  // accidental resumes when reaching for the cursor.
}

export function buildLegal(h: ScreenHost, tab: 'terms' | 'privacy' = 'terms'): void {
  clearScreen(h.host);
  const panel = el('div', { class: 'panel panel-wide panel-fixed' });
  decoratePanel(panel, `settle // ${APP_VERSION}`);
  panel.appendChild(brandHeader(h));
  panel.appendChild(screenTitle('the fine print'));

  let active: 'terms' | 'privacy' = tab;
  const tabs = el('div', { class: 'tabs' });
  const content = el('div', { class: 'tab-content section legal' });

  const renderTab = () => {
    while (content.firstChild) content.removeChild(content.firstChild);
    const sections: LegalSection[] = active === 'terms' ? TERMS : PRIVACY;
    content.appendChild(el('div', { class: 'legal-updated' }, [
      `last updated ${LEGAL_UPDATED} · contact ${LEGAL_CONTACT}`,
    ]));
    for (const sec of sections) {
      const wrap = el('div', { class: 'help-section' });
      wrap.appendChild(el('div', { class: 'help-section-title' }, [sec.title]));
      for (const para of sec.body) {
        wrap.appendChild(el('div', { class: 'help-section-body' }, [para]));
      }
      content.appendChild(wrap);
    }
  };

  (['terms', 'privacy'] as const).forEach((t) => {
    const b = el('button', { class: `tab${t === active ? ' active' : ''}` }, [t === 'terms' ? 'terms of service' : 'privacy policy']);
    b.addEventListener('click', () => {
      if (active === t) return;
      h.playMenuSfx('move');
      active = t;
      tabs.querySelectorAll('.tab').forEach((x) => x.classList.remove('active'));
      b.classList.add('active');
      renderTab();
    });
    tabs.appendChild(b);
  });
  panel.appendChild(tabs);
  panel.appendChild(content);
  renderTab();

  panel.appendChild(el('div', { class: 'row' }, [
    backButton(h, () => h.navigate({ type: 'menu' })),
  ]));
  h.host.appendChild(panel);
  const onKey = (e: KeyboardEvent) => {
    if (e.code === 'Escape') { e.preventDefault(); h.playMenuSfx('back'); h.navigate({ type: 'menu' }); }
  };
  window.addEventListener('keydown', onKey);
  registerScreenCleanup(() => window.removeEventListener('keydown', onKey));
}

/** A/B account modal: the player first chooses sign in vs create
 *  account, then gets the matching compact form. Forgot-password runs
 *  inside the sign-in branch. onSignedIn fires after any successful
 *  auth (sign-in, registration, or reset) so the caller can refresh
 *  whatever screen is behind the modal. */
export function accountModal(h: ScreenHost, onSignedIn?: () => void): void {
  const backdrop = el('div', { class: 'confirm-backdrop' });
  const body = el('div', { class: 'account-modal-body' });
  const modal = el('div', { class: 'confirm-modal account-modal' }, [body]);
  backdrop.appendChild(modal);
  document.body.appendChild(backdrop);
  requestAnimationFrame(() => backdrop.classList.add('in'));

  const close = () => {
    backdrop.classList.remove('in');
    setTimeout(() => backdrop.remove(), 200);
    document.removeEventListener('keydown', onKey, true);
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.code === 'Escape') { e.preventDefault(); close(); }
    e.stopPropagation();
  };
  document.addEventListener('keydown', onKey, true);
  backdrop.addEventListener('click', (e) => { if (e.target === backdrop) close(); });

  const clear = () => { while (body.firstChild) body.removeChild(body.firstChild); };
  const mkField = (label: string, type: string, autocomplete: string): { wrap: HTMLElement; input: HTMLInputElement } => {
    const input = el('input', { type, class: 'account-input', autocomplete, spellcheck: 'false' }) as HTMLInputElement;
    const wrap = el('label', { class: 'account-field' }, [
      el('span', { class: 'account-field-label' }, [label]), input,
    ]);
    return { wrap, input };
  };
  const succeed = (kind: 'in' | 'new' | 'reset', username: string) => {
    h.playMenuSfx('select');
    close();
    if (kind === 'new') {
      successModal({ iconName: 'profile', title: `welcome, ${username}`, body: 'your username is reserved — leaderboard submissions now rank under it.' });
    } else if (kind === 'reset') {
      successModal({ iconName: 'profile', title: 'password updated', body: `you're signed in as ${username}.` });
    }
    onSignedIn?.();
  };

  // ── Step 1: choose ──────────────────────────────────────────────
  const renderChoice = () => {
    clear();
    body.appendChild(el('div', { class: 'confirm-modal-title' }, ['account']));
    body.appendChild(el('div', { class: 'confirm-modal-body' }, [
      'an account locks your name on the global leaderboard and lets you chat. local saves stay on this device either way.',
    ]));
    const choiceIn = el('button', { class: 'btn primary account-choice' }, ['sign in']);
    const choiceNew = el('button', { class: 'btn account-choice' }, ['create account']);
    const cancel = el('button', { class: 'btn ghost' }, ['cancel']);
    choiceIn.addEventListener('click', () => { h.playMenuSfx('move'); renderSignIn(); });
    choiceNew.addEventListener('click', () => { h.playMenuSfx('move'); renderCreate(); });
    cancel.addEventListener('click', close);
    body.appendChild(el('div', { class: 'account-choice-col' }, [choiceIn, choiceNew]));
    body.appendChild(el('div', { class: 'confirm-modal-row' }, [cancel]));
  };

  // ── Step 2a: sign in (+ forgot password) ────────────────────────
  const renderSignIn = () => {
    clear();
    body.appendChild(el('div', { class: 'confirm-modal-title' }, ['sign in']));
    const user = mkField('username', 'text', 'username');
    const pass = mkField('password', 'password', 'current-password');
    const err = el('div', { class: 'feedback-error' }, ['']);
    const go = el('button', { class: 'btn primary' }, ['sign in']);
    const back = el('button', { class: 'btn ghost' }, ['back']);
    const forgot = el('button', { class: 'menu-footer-link', type: 'button', style: 'align-self: flex-start; padding-left: 0' }, ['forgot password?']);
    const doLogin = async () => {
      err.classList.remove('show');
      go.textContent = 'signing in…';
      const res = await login(user.input.value.trim(), pass.input.value);
      go.textContent = 'sign in';
      if (res.ok) succeed('in', res.username!);
      else { err.textContent = res.error ?? 'sign in failed'; err.classList.add('show'); }
    };
    go.addEventListener('click', () => { void doLogin(); });
    back.addEventListener('click', () => { h.playMenuSfx('back'); renderChoice(); });
    forgot.addEventListener('click', () => { h.playMenuSfx('move'); renderForgot(); });
    const onEnter = (e: KeyboardEvent) => { if (e.code === 'Enter') { e.preventDefault(); void doLogin(); } };
    user.input.addEventListener('keydown', onEnter);
    pass.input.addEventListener('keydown', onEnter);
    body.appendChild(el('div', { class: 'account-form' }, [user.wrap, pass.wrap, forgot, err]));
    body.appendChild(el('div', { class: 'confirm-modal-row' }, [back, go]));
    user.input.focus();
  };

  // ── Step 2b: create account ─────────────────────────────────────
  const renderCreate = () => {
    clear();
    body.appendChild(el('div', { class: 'confirm-modal-title' }, ['create account']));
    const user = mkField('username (3-16: letters, numbers, _ or -)', 'text', 'username');
    const pass = mkField('password (8+ characters)', 'password', 'new-password');
    const mail = mkField('email (optional)', 'email', 'email');
    // Age attestation — required. Under-13 accounts play and compete
    // normally, but chat is disabled (child-safety; see privacy).
    let age: 'yes' | 'no' | null = null;
    const ageYes = el('button', { class: 'btn age-choice', type: 'button' }, ["i'm 13 or older"]);
    const ageNo = el('button', { class: 'btn age-choice', type: 'button' }, ["i'm under 13"]);
    const setAge = (v: 'yes' | 'no') => {
      age = v;
      ageYes.classList.toggle('primary', v === 'yes');
      ageNo.classList.toggle('primary', v === 'no');
    };
    ageYes.addEventListener('click', () => setAge('yes'));
    ageNo.addEventListener('click', () => setAge('no'));
    const ageRow = el('div', { class: 'account-field' }, [
      el('span', { class: 'account-field-label' }, ['age (required — under-13 accounts have chat disabled)']),
      el('div', { class: 'age-choice-row' }, [ageYes, ageNo]),
    ]);
    body.appendChild(el('div', { class: 'account-form' }, [user.wrap, pass.wrap, mail.wrap, ageRow]));
    body.appendChild(el('div', { class: 'help' }, [
      'email is used only for password recovery — never for contact.',
    ]));
    const err = el('div', { class: 'feedback-error' }, ['']);
    const go = el('button', { class: 'btn primary' }, ['create account']);
    const back = el('button', { class: 'btn ghost' }, ['back']);
    const doCreate = async () => {
      err.classList.remove('show');
      if (age === null) {
        err.textContent = 'please answer the age question';
        err.classList.add('show');
        return;
      }
      go.textContent = 'creating…';
      const res = await register(user.input.value.trim(), pass.input.value, mail.input.value.trim(), age === 'yes');
      go.textContent = 'create account';
      if (res.ok) succeed('new', res.username!);
      else { err.textContent = res.error ?? 'registration failed'; err.classList.add('show'); }
    };
    go.addEventListener('click', () => { void doCreate(); });
    back.addEventListener('click', () => { h.playMenuSfx('back'); renderChoice(); });
    [user.input, pass.input, mail.input].forEach((i) =>
      i.addEventListener('keydown', (e) => { if (e.code === 'Enter') { e.preventDefault(); void doCreate(); } }));
    body.appendChild(err);
    body.appendChild(el('div', { class: 'confirm-modal-row' }, [back, go]));
    user.input.focus();
  };

  // ── Forgot password: request code → set new password ────────────
  const renderForgot = () => {
    clear();
    body.appendChild(el('div', { class: 'confirm-modal-title' }, ['reset password']));
    const user = mkField('username', 'text', 'username');
    const err = el('div', { class: 'feedback-error' }, ['']);
    const go = el('button', { class: 'btn primary' }, ['email me a reset code']);
    const back = el('button', { class: 'btn ghost' }, ['back']);
    body.appendChild(el('div', { class: 'account-form' }, [user.wrap,
      el('div', { class: 'help' }, [
        'if that account has an email on file, a 6-digit code is on its way (check spam). no email on file? contact admin@angryjuniper.cloud.',
      ]), err]));
    go.addEventListener('click', async () => {
      const uname = user.input.value.trim();
      if (!uname) { err.textContent = 'enter your username first'; err.classList.add('show'); return; }
      go.textContent = 'sending…';
      await requestReset(uname);
      // Step 2: code + new password.
      clear();
      body.appendChild(el('div', { class: 'confirm-modal-title' }, ['reset password']));
      body.appendChild(el('div', { class: 'help' }, [`resetting password for '${uname}'.`]));
      const code = mkField('6-digit code from the email', 'text', 'one-time-code');
      const pass = mkField('new password (8+ characters)', 'password', 'new-password');
      const err2 = el('div', { class: 'feedback-error' }, ['']);
      const go2 = el('button', { class: 'btn primary' }, ['set new password']);
      const back2 = el('button', { class: 'btn ghost' }, ['back']);
      go2.addEventListener('click', async () => {
        err2.classList.remove('show');
        go2.textContent = 'resetting…';
        const res = await resetPassword(uname, code.input.value.trim(), pass.input.value);
        go2.textContent = 'set new password';
        if (res.ok) succeed('reset', res.username!);
        else { err2.textContent = res.error ?? 'reset failed'; err2.classList.add('show'); }
      });
      back2.addEventListener('click', () => { h.playMenuSfx('back'); renderSignIn(); });
      body.appendChild(el('div', { class: 'account-form' }, [code.wrap, pass.wrap, err2]));
      body.appendChild(el('div', { class: 'confirm-modal-row' }, [back2, go2]));
      code.input.focus();
    });
    back.addEventListener('click', () => { h.playMenuSfx('back'); renderSignIn(); });
    body.appendChild(el('div', { class: 'confirm-modal-row' }, [back, go]));
    user.input.focus();
  };

  renderChoice();
}

/** Download the device's current save as a JSON file — the safety
 *  valve offered by the reconcile modal (and handy for manual backups
 *  generally). Pure client-side: blob → temporary <a download>. */
export function downloadLocalBackup(settings: Settings, stats: PersistedStats): void {
  try {
    const payload = {
      app: 'settle',
      version: APP_VERSION,
      exportedAt: new Date().toISOString(),
      settings,
      stats,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = el('a', { href: url, download: `settle-backup-${new Date().toISOString().slice(0, 10)}.json` }) as HTMLAnchorElement;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  } catch { /* blob/download unsupported — nothing sensible to do */ }
}

/** First-sign-in save reconciliation. Shown when this device has real
 *  local progress and the account hasn't been reconciled on this
 *  browser yet. Deliberately NOT dismissable (no escape, no backdrop
 *  click): until the player decides, neither side can safely win — a
 *  silent default is exactly the data-loss bug this modal fixes.
 *
 *  Choices surface depending on whether the account already has a
 *  cloud save:
 *    cloud save exists → merge both / use cloud / use this device
 *    cloud empty       → move local into the account / start fresh
 *  'start fresh' is confirm-gated (it factory-resets the device). */
export function reconcileModal(h: ScreenHost, opts: {
  username: string;
  cloudHasData: boolean;
  /** Called with the player's decision; resolve when applied so the
   *  busy state can clear. 'local' = upload this device's save,
   *  'cloud' = adopt the account save, 'merge' = combine both,
   *  'fresh' = factory-reset device + account save. */
  onChoose: (choice: 'merge' | 'cloud' | 'local' | 'fresh') => Promise<void>;
  exportLocal: () => void;
}): Promise<void> {
  return new Promise((resolve) => {
    const backdrop = el('div', { class: 'confirm-backdrop' });
    const body = el('div', { class: 'account-modal-body' });
    const modal = el('div', { class: 'confirm-modal account-modal reconcile-modal' }, [body]);
    backdrop.appendChild(modal);
    document.body.appendChild(backdrop);
    requestAnimationFrame(() => backdrop.classList.add('in'));
    // Swallow keys (notably Escape) — the decision is required. Capture
    // phase so game/menu handlers underneath never see them either.
    const onKey = (e: KeyboardEvent) => { e.stopPropagation(); };
    document.addEventListener('keydown', onKey, true);

    const close = () => {
      backdrop.classList.remove('in');
      setTimeout(() => backdrop.remove(), 200);
      document.removeEventListener('keydown', onKey, true);
      resolve();
    };

    let busy = false;
    const choose = async (choice: 'merge' | 'cloud' | 'local' | 'fresh', btn: HTMLElement) => {
      if (busy) return;
      if (choice === 'fresh') {
        const ok = await confirmModal({
          title: 'start fresh?',
          body: 'this resets every setting, statistic, record, and achievement on this device and starts your account with a clean save. the downloadable backup is the only way back.',
          confirmLabel: 'reset everything',
        });
        if (!ok) return;
      }
      busy = true;
      btn.classList.add('busy');
      h.playMenuSfx('select');
      await opts.onChoose(choice);
      close();
    };

    const mkChoice = (label: string, desc: string, primary: boolean): HTMLButtonElement => {
      const b = el('button', { class: `btn account-choice${primary ? ' primary' : ''}`, type: 'button' }, [label]) as HTMLButtonElement;
      b.title = desc;
      return b;
    };
    const descLine = (text: string) => el('div', { class: 'reconcile-desc' }, [text]);

    body.appendChild(el('div', { class: 'confirm-modal-title' }, [
      opts.cloudHasData ? 'sync your progress' : 'bring your progress?',
    ]));
    body.appendChild(el('div', { class: 'confirm-modal-body' }, [
      opts.cloudHasData
        ? `this device has progress saved from before you signed in, and '${opts.username}' already has a cloud save. choose what to keep — you'll only be asked once on this device.`
        : `'${opts.username}' has no cloud save yet, and this device has existing progress. move it into your account, or start completely fresh.`,
    ]));

    const col = el('div', { class: 'account-choice-col' });
    if (opts.cloudHasData) {
      const merge = mkChoice('merge both', '', true);
      merge.addEventListener('click', () => { void choose('merge', merge); });
      col.appendChild(merge);
      col.appendChild(descLine('records, achievements, and totals combine — the best of each side wins'));
      const cloud = mkChoice('use cloud save', '', false);
      cloud.addEventListener('click', () => { void choose('cloud', cloud); });
      col.appendChild(cloud);
      col.appendChild(descLine("adopt your account's save — this device's extra progress is discarded"));
      const local = mkChoice("use this device's save", '', false);
      local.addEventListener('click', () => { void choose('local', local); });
      col.appendChild(local);
      col.appendChild(descLine("replace your cloud save with what's on this device"));
    } else {
      const local = mkChoice('move my progress into this account', '', true);
      local.addEventListener('click', () => { void choose('local', local); });
      col.appendChild(local);
      col.appendChild(descLine('records, stats, and achievements upload to your account'));
      const fresh = mkChoice('start fresh', '', false);
      fresh.addEventListener('click', () => { void choose('fresh', fresh); });
      col.appendChild(fresh);
      col.appendChild(descLine('reset this device and begin a clean save on your account'));
    }
    body.appendChild(col);

    const backup = el('button', { class: 'menu-footer-link reconcile-backup', type: 'button' }, [
      "download a backup of this device's data first",
    ]);
    backup.addEventListener('click', () => {
      opts.exportLocal();
      backup.textContent = 'backup downloaded ✓';
    });
    body.appendChild(el('div', { class: 'reconcile-backup-row' }, [backup]));
  });
}

export function buildAccount(h: ScreenHost): void {
  clearScreen(h.host);
  const panel = el('div', { class: 'panel' });
  decoratePanel(panel, `settle // ${APP_VERSION}`);
  panel.appendChild(brandHeader(h));
  panel.appendChild(screenTitle('account'));

  const acct = getAccount();
  if (acct) {
    panel.appendChild(el('div', { class: 'account-summary' }, [
      el('div', { class: 'account-row' }, [
        el('span', { class: 'account-label' }, ['signed in as']),
        el('span', { class: 'account-value' }, [acct.username]),
      ]),
      el('div', { class: 'help', style: 'margin-top: 10px' }, [
        'your leaderboard entries are tied to this account — submissions always rank under this name, and nobody else can claim it.',
      ]),
    ]));
    const signOut = el('button', { class: 'btn ghost', style: 'margin-top: 14px' }, ['sign out']);
    signOut.addEventListener('click', async () => {
      signOut.textContent = 'signing out…';
      await logout();
      h.playMenuSfx('back');
      h.navigate({ type: 'menu' });
    });
    // Save management: a quiet backup download plus the confirm-gated
    // start-fresh reset (device AND account save go back to factory
    // defaults — the always-available twin of the first-sign-in
    // reconcile choice).
    const backup = el('button', { class: 'btn ghost', style: 'margin-top: 14px; margin-left: 10px' }, ['download save backup']);
    backup.addEventListener('click', () => {
      h.playMenuSfx('select');
      downloadLocalBackup(h.settings, h.stats);
    });
    const fresh = el('button', { class: 'btn ghost', style: 'margin-top: 14px; margin-left: 10px' }, ['start fresh']);
    fresh.addEventListener('click', async () => {
      const ok = await confirmModal({
        title: 'start fresh?',
        body: 'every setting, statistic, record, and achievement resets to factory defaults — on this device and on your account cloud save. download a backup first if you might want any of it back.',
        confirmLabel: 'reset everything',
      });
      if (!ok) return;
      Object.assign(h.settings, structuredClone(DEFAULT_SETTINGS));
      Object.assign(h.stats, structuredClone(DEFAULT_STATS));
      // saveSettings/saveStats fire the save hook, which schedules the
      // cloud push — the wiped state propagates to the account without
      // any extra plumbing here.
      h.saveSettings();
      h.saveStats();
      h.onAudioChange();
      h.onVideoChange();
      h.onHandlingChange();
      h.onPaletteChange();
      h.playMenuSfx('back');
      h.navigate({ type: 'account' });
    });
    panel.appendChild(el('div', {}, [signOut, backup, fresh]));
  } else {
    panel.appendChild(el('div', { class: 'help', style: 'margin-bottom: 12px' }, [
      "you're not signed in. an account locks your name on the global leaderboard and lets you chat — local saves stay on this device either way.",
    ]));
    const open = el('button', { class: 'btn primary' }, ['sign in or create account']);
    open.addEventListener('click', () => {
      h.playMenuSfx('select');
      accountModal(h, () => h.navigate({ type: 'account' }));
    });
    panel.appendChild(open);
  }

  panel.appendChild(el('div', { class: 'row' }, [
    backButton(h, () => h.navigate({ type: 'menu' })),
  ]));
  h.host.appendChild(panel);
  const onKey = (e: KeyboardEvent) => {
    const t = e.target as HTMLElement | null;
    if (e.code === 'Escape' && (!t || t.tagName !== 'INPUT')) {
      e.preventDefault(); h.playMenuSfx('back'); h.navigate({ type: 'menu' });
    }
  };
  window.addEventListener('keydown', onKey);
  registerScreenCleanup(() => window.removeEventListener('keydown', onKey));
}

export function buildChangelog(h: ScreenHost): void {
  clearScreen(h.host);
  const panel = el('div', { class: 'panel panel-wide panel-fixed' });
  decoratePanel(panel, `settle // ${APP_VERSION}`);
  panel.appendChild(brandHeader(h));
  panel.appendChild(screenTitle("what's new"));

  const wrap = el('div', { class: 'tab-content section changelog' });
  CHANGELOG.forEach((entry, i) => {
    const head = el('div', { class: 'changelog-version' }, [
      el('span', { class: 'changelog-tag' }, [`v${entry.version}`]),
      i === 0 ? el('span', { class: 'changelog-current' }, ['current']) : null,
      el('span', { class: 'changelog-date' }, [entry.date]),
    ]);
    const list = el('ul', { class: 'changelog-items' });
    for (const item of entry.items) list.appendChild(el('li', {}, [item]));
    wrap.appendChild(el('div', { class: 'changelog-entry' }, [head, list]));
  });
  panel.appendChild(wrap);

  panel.appendChild(el('div', { class: 'row' }, [
    backButton(h, () => h.navigate({ type: 'menu' })),
  ]));
  h.host.appendChild(panel);
  const onKey = (e: KeyboardEvent) => {
    if (e.code === 'Escape') { e.preventDefault(); h.playMenuSfx('back'); h.navigate({ type: 'menu' }); }
  };
  window.addEventListener('keydown', onKey);
  registerScreenCleanup(() => window.removeEventListener('keydown', onKey));
}

export function buildLeaderboard(h: ScreenHost): void {
  clearScreen(h.host);
  const panel = el('div', { class: 'panel panel-wide panel-fixed' });
  decoratePanel(panel, `settle // ${APP_VERSION}`);
  panel.appendChild(brandHeader(h));
  panel.appendChild(screenTitle('leaderboard'));

  // Mode tabs — one per submittable mode (zen never appears).
  let activeMode = LEADERBOARD_MODES[0]!.id;
  const tabs = el('div', { class: 'tabs' });
  const content = el('div', { class: 'tab-content section' });

  const renderBoard = async () => {
    const mode = activeMode;
    while (content.firstChild) content.removeChild(content.firstChild);
    content.appendChild(el('div', { class: 'lb-status' }, ['loading…']));
    const page = await fetchLeaderboard(mode);
    // The user may have switched tabs while the fetch was in flight.
    if (mode !== activeMode) return;
    while (content.firstChild) content.removeChild(content.firstChild);
    if (!page.ok) {
      content.appendChild(el('div', { class: 'lb-status' }, [
        'leaderboard unavailable right now — check back soon.',
      ]));
      return;
    }
    if (page.entries.length === 0) {
      content.appendChild(el('div', { class: 'lb-status' }, [
        'no runs on the board yet. finish a run and submit yours!',
      ]));
      return;
    }
    const metricLabel = LEADERBOARD_MODES.find((m) => m.id === mode)?.metric === 'time' ? 'time' : 'score';
    const list = el('div', { class: 'lb-list' });
    list.appendChild(el('div', { class: 'lb-row lb-head' }, [
      el('span', { class: 'lb-rank' }, ['#']),
      el('span', { class: 'lb-name' }, ['player']),
      el('span', { class: 'lb-value' }, [metricLabel]),
      el('span', { class: 'lb-when' }, ['date']),
    ]));
    const youName = page.you?.name;
    const youRank = page.you?.rank;
    for (const e of page.entries) {
      const isYou = youRank != null && e.rank === youRank && e.name === youName;
      list.appendChild(el('div', { class: `lb-row${isYou ? ' you' : ''}` }, [
        el('span', { class: 'lb-rank' }, [`${e.rank}`]),
        el('span', { class: 'lb-name' }, [e.name]),
        el('span', { class: 'lb-value' }, [formatLeaderboardValue(mode, e.value)]),
        el('span', { class: 'lb-when' }, [new Date(e.when).toLocaleDateString()]),
      ]));
    }
    content.appendChild(list);
    // Player's own placement when it didn't make the visible page.
    if (page.you && page.you.rank > page.entries.length) {
      content.appendChild(el('div', { class: 'lb-you-line' }, [
        `your best: #${page.you.rank} of ${page.total} — ${formatLeaderboardValue(mode, page.you.value)}`,
      ]));
    } else {
      content.appendChild(el('div', { class: 'lb-you-line dim' }, [
        `${page.total} player${page.total === 1 ? '' : 's'} on the board`,
      ]));
    }
  };

  LEADERBOARD_MODES.forEach((m, i) => {
    const b = el('button', { class: `tab${i === 0 ? ' active' : ''}` }, [m.label]);
    b.addEventListener('click', () => {
      if (activeMode === m.id) return;
      h.playMenuSfx('move');
      activeMode = m.id;
      tabs.querySelectorAll('.tab').forEach((x) => x.classList.remove('active'));
      b.classList.add('active');
      void renderBoard();
    });
    tabs.appendChild(b);
  });
  panel.appendChild(tabs);
  panel.appendChild(el('div', { class: 'settlers-online-count lb-online' }, ['']));
  panel.appendChild(content);
  void renderBoard();

  panel.appendChild(el('div', { class: 'row' }, [
    backButton(h, () => h.navigate({ type: 'menu' })),
  ]));
  h.host.appendChild(panel);
  const onKey = (e: KeyboardEvent) => {
    if (e.code === 'Escape') { e.preventDefault(); h.playMenuSfx('back'); h.navigate({ type: 'menu' }); }
  };
  window.addEventListener('keydown', onKey);
  registerScreenCleanup(() => window.removeEventListener('keydown', onKey));
}

/** Submission context the results screen renders when a run qualifies
 *  for the leaderboard. The closure owns the actual POST; the screen
 *  only manages button state. */
export interface ResultsSubmitCtx {
  /** Formatted value being submitted, e.g. "1:23.45" or "128,540". */
  valueText: string;
  doSubmit: () => Promise<SubmitResult>;
  /** True when the run qualifies but no account session exists — the
   *  results screen renders a 'sign in to submit' nav button instead
   *  of the live submit. */
  signedOut?: boolean;
}

export function buildResults(
  h: ScreenHost, modeId: ModeId, result: ModeResultDetails, onPlayAgain: () => void,
  submit?: ResultsSubmitCtx,
): void {
  clearScreen(h.host);
  const panel = el("div", { class: "panel" }); decoratePanel(panel, `settle // ${APP_VERSION}`);
  // The orphaned 'cheese · garbage cleared' style subtitle has been
  // removed — the primary metric + four stats below carry the context.
  const mode = getMode(modeId);
  panel.appendChild(brandHeader(h)); panel.appendChild(screenTitle('results'));
  if (result.newRecord) {
    panel.appendChild(el('div', { class: 'result-new-record-wrap' }, [
      el('div', { class: 'result-new-record' }, [icon('trophy-solid'), 'new personal best']),
    ]));
  }
  panel.appendChild(el('div', { class: `result-primary${result.newRecord ? ' new-record' : ''}` }, [
    el('div', { class: 'label' }, [result.primaryMetric.label.toLowerCase()]),
    el('div', { class: 'value' }, [result.primaryMetric.value]),
  ]));
  // Previous-best comparison line. Shows the prior PB and the improvement
  // delta whenever we have a comparable value. Hidden for modes without
  // a PB (Zen) or when the run wasn't comparable (e.g. didn't reach 40
  // lines in Sprint).
  if (result.prevBestRaw != null && result.currentRaw != null) {
    const lower = !!result.pbLowerBetter;
    const fmt = lower
      ? (v: number) => formatTime(v)
      : (v: number) => v.toLocaleString();
    const delta = result.currentRaw - result.prevBestRaw;
    const improved = lower ? delta < 0 : delta > 0;
    const deltaTxt = lower
      ? `${delta < 0 ? '−' : '+'}${formatTime(Math.abs(delta))}`
      : `${delta > 0 ? '+' : ''}${delta.toLocaleString()}`;
    panel.appendChild(el('div', { class: 'result-pb-compare' }, [
      el('span', { class: 'pb-label' }, ['previous best']),
      el('span', { class: 'pb-value' }, [fmt(result.prevBestRaw)]),
      el('span', { class: `pb-delta ${improved ? 'better' : 'worse'}` }, [deltaTxt]),
    ]));
  } else if (result.currentRaw != null && result.prevBestRaw == null && mode.pbKey) {
    panel.appendChild(el('div', { class: 'result-pb-compare' }, [
      el('span', { class: 'pb-label' }, ['first qualifying run']),
      el('span', { class: 'pb-delta better' }, ['★ baseline set']),
    ]));
  }
  const grid = el('div', { class: 'results-grid' });
  for (const s of result.secondaryMetrics) {
    grid.appendChild(el('div', { class: 'result-stat' }, [
      el('div', { class: 'label' }, [s.label.toLowerCase()]),
      el('div', { class: 'value' }, [s.value]),
    ]));
  }
  panel.appendChild(grid);
  const row = el('div', { class: 'row' }, [
    el('button', { class: 'btn primary', onclick: onPlayAgain }, ['play again']),
    el('button', { class: 'btn', onclick: () => h.navigate({ type: 'modes' }) }, ['change mode']),
    el('button', { class: 'btn ghost', onclick: () => h.navigate({ type: 'menu' }) }, ['menu']),
  ]);
  // Optional leaderboard submission — present only when the run
  // qualifies (named profile, non-zen mode, objective met for the
  // time-based modes). Submission is one-shot; the button narrates
  // its own state transitions.
  if (submit && submit.signedOut) {
    const btn = el('button', { class: 'btn btn-with-icon lb-submit' }, [
      icon('profile'), el('span', {}, ['sign in to submit score']),
    ]);
    btn.addEventListener('click', () => { h.playMenuSfx('select'); h.navigate({ type: 'account' }); });
    row.insertBefore(btn, row.children[1] ?? null);
  } else if (submit) {
    // Keep a direct reference to the label span — querySelector('span')
    // would match the icon helper's wrapper span first and inject the
    // status text INTO the icon (the overlapping-text bug). Label text
    // stays generic ('submit score') so the button reads the same shape
    // whether the metric is points or a time.
    const label = el('span', {}, ['submit score']);
    const btn = el('button', { class: 'btn btn-with-icon lb-submit' }, [
      icon('trophy-solid'), label,
    ]);
    let busy = false;
    btn.addEventListener('click', async () => {
      if (busy) return;
      busy = true;
      btn.classList.add('busy');
      label.textContent = 'submitting…';
      const res = await submit.doSubmit();
      btn.classList.remove('busy');
      if (res.ok) {
        h.playMenuSfx('select');
        btn.classList.add('done');
        label.textContent = 'submitted';
        // Rank confirmation as a themed popup (matches the feedback
        // confirmation; auto-dismisses). Button stays disabled — the
        // run is on the board, nothing more to send.
        successModal(res.improved === false
          ? {
              iconName: 'trophy-solid',
              title: 'already on the board',
              body: `a previous run of yours ranks #${res.rank} of ${res.total} — beat it to move up.`,
            }
          : {
              iconName: 'trophy-solid',
              title: 'score submitted',
              body: `${submit.valueText} — ranked #${res.rank} of ${res.total}.`,
            });
      } else {
        busy = false;
        label.textContent = 'submit failed — retry';
        // Failures get a proper modal (a bottom toast is too easy to
        // miss and formats poorly for multi-clause errors).
        void alertModal(res.error === 'leaderboard not configured'
          ? {
              title: 'leaderboard unavailable',
              body: "the global leaderboard isn't set up on the server yet. your run is saved locally — try submitting again after the next update.",
            }
          : {
              title: 'submission failed',
              body: "your score couldn't reach the leaderboard. check your connection and try again — the submit button is still active.",
              detail: res.error ?? 'unknown error',
            });
      }
    });
    row.insertBefore(btn, row.children[1] ?? null);
  }
  panel.appendChild(row);
  h.host.appendChild(panel);
  const onKey = (e: KeyboardEvent) => {
    if (e.code === 'Enter' || e.code === 'KeyR') { e.preventDefault(); onPlayAgain(); }
    else if (e.code === 'Escape') { e.preventDefault(); h.navigate({ type: 'menu' }); }
    else if (e.code === 'KeyM') { e.preventDefault(); h.navigate({ type: 'modes' }); }
  };
  window.addEventListener('keydown', onKey);
  registerScreenCleanup(() => window.removeEventListener('keydown', onKey));
}

// ---------- helpers ----------
function statRow(label: string, value: string, highlight = false): HTMLElement {
  return el('div', { class: 'stat-row' }, [
    el('div', { class: 'label' }, [label.toLowerCase()]),
    el('div', { class: `value${highlight ? ' pb' : ''}` }, [value]),
  ]);
}

function backButton(h: ScreenHost | (() => void), onclickArg?: () => void): HTMLElement {
  // Overloaded so existing callers that only pass an onclick keep working.
  // Preferred form: backButton(h, () => ...) so we can play the menu_back
  // sfx for consistency with the other menu navigation feedback.
  const onclick = typeof h === 'function' ? h : onclickArg!;
  const host = typeof h === 'function' ? null : h;
  const wrapped = () => {
    host?.playMenuSfx('back');
    onclick();
  };
  return el('button', { class: 'btn btn-with-icon', onclick: wrapped }, [icon('back'), el('span', {}, ['back'])]);
}

async function resetAllSettings(h: ScreenHost): Promise<void> {
  const ok = await confirmModal({
    title: 'reset all settings?',
    body: 'every audio, video, theme, handling, control, and general setting on this profile returns to its factory value. profile, stats, and unlocked achievements are kept.',
    confirmLabel: 'reset everything',
  });
  if (!ok) return;
  Object.assign(h.settings, structuredClone(DEFAULT_SETTINGS));
  h.saveSettings();
  h.onAudioChange();
  h.onVideoChange();
  h.onHandlingChange();
  h.onPaletteChange();
  h.onMusicVariantChange();
}

type SettingsTabId = 'general' | 'video' | 'audio' | 'themes' | 'handling' | 'controls';

/** Reset only the fields owned by a single settings tab. Confirms via
 *  modal, then rebuilds the tab so every slider / toggle shows the
 *  new default value. */
async function resetTab(h: ScreenHost, tab: SettingsTabId): Promise<void> {
  const label = tab === 'themes' ? 'theme' : tab;
  const ok = await confirmModal({
    title: `reset ${label} settings?`,
    body: `every ${label} setting on this profile returns to its default value.`,
    confirmLabel: 'reset',
  });
  if (!ok) return;
  const def = DEFAULT_SETTINGS;
  switch (tab) {
    case 'general':
      h.settings.countdownEnabled = def.countdownEnabled;
      h.settings.chatFilter = def.chatFilter;
      h.settings.resumeCountdownEnabled = def.resumeCountdownEnabled;
      h.settings.showFps = def.showFps;
      h.settings.showFinesseHints = def.showFinesseHints;
      h.settings.highFramerate = def.highFramerate;
      h.settings.render.showGhost = def.render.showGhost;
      h.settings.render.showGrid = def.render.showGrid;
      h.settings.render.ghostAlpha = def.render.ghostAlpha;
      h.onVideoChange();
      break;
    case 'video':
      h.settings.postfx = { ...def.postfx };
      h.settings.renderScale = def.renderScale;
      h.onVideoChange();
      break;
    case 'audio':
      h.settings.audio = structuredClone(def.audio);
      h.settings.radio = { ...def.radio };
      h.onAudioChange();
      h.onMusicVariantChange();
      break;
    case 'themes':
      h.settings.paletteId = def.paletteId;
      h.settings.render.rgbCellStyle = def.render.rgbCellStyle;
      h.onPaletteChange();
      h.onVideoChange();
      break;
    case 'handling':
      h.settings.handling = { ...def.handling };
      h.onHandlingChange();
      break;
    case 'controls':
      h.settings.keymap = structuredClone(DEFAULT_KEYMAP);
      break;
  }
  h.saveSettings();
  // Rebuild the tab so widgets reflect the new state immediately.
  h.navigate({ type: 'settings', tab });
}

/** Append a uniform 'reset to default' button to the bottom of a tab. */
function appendTabResetButton(content: HTMLElement, h: ScreenHost, tab: SettingsTabId): void {
  const btn = el('button', { class: 'btn ghost', style: 'margin-top: 14px' }, ['reset page settings to default']);
  btn.addEventListener('click', () => { void resetTab(h, tab); });
  content.appendChild(btn);
}

function enableKeyboardMenuNav(
  panel: HTMLElement, selector: string, handlers: (() => void)[], onBack?: () => void,
  hooks?: { onMove?: () => void; onSelect?: () => void; onBack?: () => void },
): void {
  const items = Array.from(panel.querySelectorAll<HTMLElement>(selector));
  let focus = items.findIndex((i) => i.classList.contains('focus'));
  if (focus < 0) focus = 0;
  const setFocus = (i: number) => {
    items[focus]?.classList.remove('focus');
    focus = (i + items.length) % items.length;
    items[focus]?.classList.add('focus');
    items[focus]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    hooks?.onMove?.();
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.code === 'ArrowDown' || e.code === 'KeyJ') { e.preventDefault(); setFocus(focus + 1); }
    else if (e.code === 'ArrowUp' || e.code === 'KeyK') { e.preventDefault(); setFocus(focus - 1); }
    else if (e.code === 'Enter' || e.code === 'Space') {
      e.preventDefault();
      hooks?.onSelect?.();
      handlers[focus]?.();
    } else if (e.code === 'Escape' && onBack) {
      e.preventDefault();
      hooks?.onBack?.();
      onBack();
    }
  };
  window.addEventListener('keydown', onKey, { once: false });
  registerScreenCleanup(() => window.removeEventListener('keydown', onKey));
}

/** Arrow-key roving focus across widgets inside a settings tab content.
 *  Toggles, sliders, selects, palette cards, keybind buttons.
 *  ←/→ adjusts sliders; ↑/↓ moves between widgets; Enter activates toggles.
 *  Focus ring is invisible until the first arrow press so clicking on any
 *  widget doesn't show a stale ring on a different one. */
function enableSettingsKeyboardNav(content: HTMLElement): void {
  const widgets = Array.from(content.querySelectorAll<HTMLElement>('.toggle, .palette-card, input[type=range], select, .keybind-btn'));
  if (widgets.length === 0) return;
  let focus = 0;
  let focusVisible = false;
  const setFocus = (i: number) => {
    widgets[focus]?.classList.remove('kbd-focus');
    focus = (i + widgets.length) % widgets.length;
    const w = widgets[focus]!;
    if (focusVisible) w.classList.add('kbd-focus');
    if (typeof (w as HTMLInputElement).focus === 'function') (w as HTMLInputElement).focus({ preventScroll: true });
    w.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  };
  // Clear focus ring on any pointer interaction.
  const onPointer = () => {
    focusVisible = false;
    widgets.forEach((w) => w.classList.remove('kbd-focus'));
  };
  content.addEventListener('pointerdown', onPointer);
  registerScreenCleanup(() => content.removeEventListener('pointerdown', onPointer));
  const onKey = (e: KeyboardEvent) => {
    // Ignore if user is typing in a text input.
    const tgt = e.target as HTMLElement | null;
    if (tgt && (tgt.tagName === 'INPUT' && (tgt as HTMLInputElement).type === 'text')) return;
    if (e.code === 'ArrowDown' || e.code === 'KeyJ') { e.preventDefault(); focusVisible = true; setFocus(focus + 1); return; }
    if (e.code === 'ArrowUp'   || e.code === 'KeyK') { e.preventDefault(); focusVisible = true; setFocus(focus - 1); return; }
    const w = widgets[focus];
    if (!w) return;
    if (e.code === 'ArrowLeft' || e.code === 'ArrowRight') {
      // Adjust slider or step through select options.
      if (w.tagName === 'INPUT' && (w as HTMLInputElement).type === 'range') {
        e.preventDefault();
        const r = w as HTMLInputElement;
        const step = parseFloat(r.step || '1') || 1;
        const min = parseFloat(r.min || '0');
        const max = parseFloat(r.max || '100');
        const cur = parseFloat(r.value);
        const dir = e.code === 'ArrowLeft' ? -1 : 1;
        const mult = e.shiftKey ? 10 : 1;
        const next = Math.max(min, Math.min(max, cur + dir * step * mult));
        r.value = String(next);
        r.dispatchEvent(new Event('input', { bubbles: true }));
      } else if (w.tagName === 'SELECT') {
        e.preventDefault();
        const s = w as HTMLSelectElement;
        const dir = e.code === 'ArrowLeft' ? -1 : 1;
        const next = (s.selectedIndex + dir + s.options.length) % s.options.length;
        s.selectedIndex = next;
        s.dispatchEvent(new Event('change', { bubbles: true }));
      }
    } else if (e.code === 'Enter' || e.code === 'Space') {
      if (w.classList.contains('toggle') || w.classList.contains('palette-card') || w.classList.contains('keybind-btn')) {
        e.preventDefault();
        w.click();
      }
    }
  };
  window.addEventListener('keydown', onKey, { once: false });
  registerScreenCleanup(() => window.removeEventListener('keydown', onKey));
}

// ─── Profile select / create screen ──────────────────────────────────
// Shown on first launch (no active profile) and from a "switch profile"
// button. Lets the player pick from existing local profiles or create a
// new one. Each profile gets its own settings + stats namespace.
export function buildProfileSelect(
  h: ScreenHost,
  ctx: {
    profiles: ProfileRecord[];
    activeId: string | null;
    onCreate: (name: string) => void;
    onChoose: (id: string) => void;
    onDelete: (id: string) => void;
    onGuest: () => void;
  },
): void {
  clearScreen(h.host);
  const panel = el('div', { class: 'panel panel-wide' });
  decoratePanel(panel, `settle // ${APP_VERSION}`);
  panel.appendChild(brandHeader(h));
  panel.appendChild(screenTitle('who\'s playing?'));
  panel.appendChild(el('div', { class: 'panel-subtitle' }, [
    ctx.profiles.length === 0
      ? 'create a profile to save your settings and records'
      : 'select a profile or create a new one',
  ]));

  // List existing profiles (most recently played first).
  if (ctx.profiles.length > 0) {
    const list = el('div', { class: 'menu-list', style: 'margin-bottom: 18px' });
    const sorted = ctx.profiles.slice().sort((a, b) => b.lastPlayedAt - a.lastPlayedAt);
    sorted.forEach((p, i) => {
      const lastPlayed = humanTimeAgo(p.lastPlayedAt);
      const isActive = p.id === ctx.activeId;
      const item = el('div', { class: `menu-item${i === 0 ? ' focus' : ''}` }, [
        el('div', { class: 'menu-icon' }, ['◈']),
        el('div', { class: 'menu-body' }, [
          el('div', { class: 'menu-label' }, [p.name + (isActive ? ' · active' : '')]),
          el('div', { class: 'menu-desc' }, [`last played ${lastPlayed}`]),
        ]),
        el('div', { class: 'profile-delete', title: 'delete this profile' }, ['×']),
      ]);
      // Click the row to choose; click the delete affordance to remove.
      item.addEventListener('click', async (e) => {
        const target = e.target as HTMLElement;
        if (target.closest('.profile-delete')) {
          const ok = await confirmModal({
            title: 'delete profile?',
            body: `"${p.name}" and all of its settings, stats, and unlocked achievements will be permanently removed from this browser.`,
            confirmLabel: 'delete profile',
          });
          if (!ok) return;
          h.playMenuSfx('back');
          ctx.onDelete(p.id);
          return;
        }
        h.playMenuSfx('select');
        ctx.onChoose(p.id);
      });
      list.appendChild(item);
    });
    panel.appendChild(list);
  }

  // New-profile creation row.
  panel.appendChild(el('div', { class: 'section-divider' }, [el('span', {}, ['new profile'])]));
  const inputRow = el('div', { class: 'profile-create-row' });
  const nameInput = el('input', {
    type: 'text',
    class: 'profile-name-input',
    placeholder: 'enter a name…',
    maxlength: '24',
  }) as HTMLInputElement;
  nameInput.autocomplete = 'off';
  nameInput.spellcheck = false;
  const createBtn = el('button', { class: 'btn primary' }, ['create']);
  const submit = () => {
    const name = nameInput.value.trim();
    if (!name) {
      nameInput.classList.add('shake');
      setTimeout(() => nameInput.classList.remove('shake'), 380);
      return;
    }
    h.playMenuSfx('select');
    ctx.onCreate(name);
  };
  createBtn.addEventListener('click', submit);
  nameInput.addEventListener('keydown', (e) => {
    if (e.code === 'Enter') { e.preventDefault(); submit(); }
  });
  inputRow.appendChild(nameInput);
  inputRow.appendChild(createBtn);
  panel.appendChild(inputRow);

  // Guest entry — discards any settings or stats made during the session
  // when the player switches profile or reloads the page. Useful for
  // visitors / shared kiosks where you don't want to clutter the
  // profile list. Lives below the create row, separated by a thin rule.
  panel.appendChild(el('div', { class: 'profile-guest-row' }, [
    el('div', { class: 'profile-guest-text' }, ['just visiting?']),
    (() => {
      const btn = el('button', { class: 'btn ghost profile-guest-btn' }, ['play as guest']);
      btn.addEventListener('click', () => {
        h.playMenuSfx('select');
        ctx.onGuest();
      });
      return btn;
    })(),
  ]));
  panel.appendChild(el('div', { class: 'help', style: 'margin-top: 6px; font-size: 10px; letter-spacing: 0.10em;' }, [
    'guest sessions don\'t save anything — your settings, custom palettes, stats, and achievements all reset when you reload or switch profile.',
  ]));

  // Focus the input for first-launch quick entry.
  setTimeout(() => nameInput.focus(), 50);

  h.host.appendChild(panel);
}

/** Compact "5m ago" / "2h ago" / "3d ago" / "just now" formatter. */
function humanTimeAgo(ms: number): string {
  const diff = Date.now() - ms;
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return `${Math.floor(diff / 86_400_000)}d ago`;
}

// ─── Custom palette manager / editor ────────────────────────────────
// Listed in the themes tab as a 12th "custom" tile that routes here.
// Lets the player save multiple custom palettes per profile, edit
// them, set them active, or delete them.
import type { ThemePalette as ThemePaletteForCustom } from '../render/palettes';
import { derivePaletteFields } from '../render/palettes';

export function buildCustomPalettes(
  h: ScreenHost,
  ctx: {
    customs: ThemePaletteForCustom[];
    activeId: string;
    onSave: (palette: ThemePaletteForCustom) => void;
    onActivate: (id: string) => void;
    onDelete: (id: string) => void;
  },
): void {
  clearScreen(h.host);
  const panel = el('div', { class: 'panel panel-wide' });
  decoratePanel(panel, `settle // ${APP_VERSION}`);
  panel.appendChild(brandHeader(h)); panel.appendChild(screenTitle('custom palettes'));

  let editing: ThemePaletteForCustom | null = null;
  let editingIsNew = false;
  const root = el('div', { class: 'custom-palette-root' });
  panel.appendChild(root);

  const render = () => {
    while (root.firstChild) root.removeChild(root.firstChild);
    if (editing) {
      renderEditor();
    } else {
      renderList();
    }
  };

  const renderList = () => {
    if (ctx.customs.length === 0) {
      root.appendChild(el('div', { class: 'help', style: 'margin-bottom: 16px; line-height: 1.6' }, [
        'create your own color palette by picking accent colors, tetrimino piece colors, and a background. saved palettes appear alongside the built-in themes.',
      ]));
    }
    const grid = el('div', { class: 'palette-grid' });
    const activeStyle = h.settings.render.rgbCellStyle;
    ctx.customs.forEach((p) => {
      const card = el('div', { class: `palette-card${p.id === ctx.activeId ? ' active' : ''}` });
      card.appendChild(el('div', { class: 'name' }, [p.name]));
      const sw = el('div', { class: 'palette-swatches' });
      for (const t of ['I', 'O', 'T', 'S', 'Z', 'J', 'L'] as const) {
        sw.appendChild(el('div', { style: swatchCss(p.pieces[t], activeStyle) }));
      }
      card.appendChild(sw);
      const row = el('div', { class: 'palette-card-actions' }, [
        el('button', { type: 'button', class: 'btn ghost' }, ['select']),
        el('button', { type: 'button', class: 'btn ghost' }, ['edit']),
        el('button', { type: 'button', class: 'btn ghost danger' }, ['delete']),
      ]);
      (row.children[0] as HTMLButtonElement).addEventListener('click', (e) => { e.stopPropagation(); h.playMenuSfx('select'); ctx.onActivate(p.id); });
      (row.children[1] as HTMLButtonElement).addEventListener('click', (e) => { e.stopPropagation(); h.playMenuSfx('move'); editing = { ...p, pieces: { ...p.pieces } }; editingIsNew = false; render(); });
      (row.children[2] as HTMLButtonElement).addEventListener('click', async (e) => {
        e.stopPropagation();
        const ok = await confirmModal({
          title: 'delete custom palette?',
          body: `"${p.name}" will be permanently removed from this profile.`,
          confirmLabel: 'delete palette',
        });
        if (ok) ctx.onDelete(p.id);
      });
      card.appendChild(row);
      grid.appendChild(card);
    });
    root.appendChild(grid);
    const actionRow = el('div', { class: 'row', style: 'margin-top: 18px' });
    const newBtn = el('button', { class: 'btn primary' }, ['+ new palette']);
    newBtn.addEventListener('click', () => {
      h.playMenuSfx('select');
      editing = derivePaletteFields({
        bg: '#0a0612',
        accent: '#ff3da6',
        accent2: '#00e6ff',
        danger: '#ff5566',
        pieces: { I: '#00e6ff', O: '#ffd23f', T: '#c060ff', S: '#5fff8e', Z: '#ff4d72', J: '#3d7dff', L: '#ff9933' },
      });
      editing.name = 'new palette';
      editingIsNew = true;
      render();
    });
    actionRow.appendChild(newBtn);
    const backBtn = el('button', { class: 'btn' }, ['← back to themes']);
    backBtn.addEventListener('click', () => { h.playMenuSfx('back'); h.navigate({ type: 'settings', tab: 'themes' }); });
    actionRow.appendChild(backBtn);
    root.appendChild(actionRow);
  };

  const renderEditor = () => {
    if (!editing) return;
    const p = editing;
    // Live preview row at the top — shows piece swatches updated in
    // real time so the user can see what they're building.
    const preview = el('div', { class: 'custom-palette-preview' });
    const renderPreview = () => {
      while (preview.firstChild) preview.removeChild(preview.firstChild);
      preview.style.background = p.bg;
      preview.style.borderColor = p.accent;
      const swRow = el('div', { class: 'custom-palette-preview-swatches' });
      for (const t of ['I', 'O', 'T', 'S', 'Z', 'J', 'L'] as const) {
        swRow.appendChild(el('div', { style: swatchCss(p.pieces[t], h.settings.render.rgbCellStyle) }));
      }
      preview.appendChild(swRow);
      const accents = el('div', { class: 'custom-palette-preview-accents' }, [
        el('div', { style: `background: ${p.accent}` }),
        el('div', { style: `background: ${p.accent2}` }),
        el('div', { style: `background: ${p.danger}` }),
      ]);
      preview.appendChild(accents);
    };
    renderPreview();
    root.appendChild(preview);

    // Name input
    const nameInput = el('input', {
      type: 'text', class: 'profile-name-input', value: p.name, maxlength: '24',
    }) as HTMLInputElement;
    nameInput.addEventListener('input', () => { p.name = nameInput.value.trim() || 'untitled'; });
    root.appendChild(el('div', { class: 'sfx-tune-group' }, ['name']));
    root.appendChild(nameInput);

    // Color picker row factory
    const colorRow = (label: string, key: 'bg' | 'accent' | 'accent2' | 'danger' | `pieces.${'I'|'O'|'T'|'S'|'Z'|'J'|'L'}`) => {
      const get = (): string => {
        if (key.startsWith('pieces.')) return p.pieces[key.slice(7) as 'I'];
        return (p as any)[key];
      };
      const set = (v: string) => {
        if (key.startsWith('pieces.')) p.pieces[key.slice(7) as 'I'] = v;
        else (p as any)[key] = v;
        // Re-derive secondaries when bg changes.
        if (key === 'bg' || key === 'accent' || key === 'accent2' || key === 'danger') {
          const refreshed = derivePaletteFields({
            bg: p.bg, accent: p.accent, accent2: p.accent2, danger: p.danger, pieces: p.pieces,
          });
          p.bg2 = refreshed.bg2; p.grid = refreshed.grid; p.gridStrong = refreshed.gridStrong;
          p.text = refreshed.text; p.textDim = refreshed.textDim; p.ghost = refreshed.ghost;
        }
        renderPreview();
      };
      const input = el('input', { type: 'color', value: get(), class: 'custom-palette-color' }) as HTMLInputElement;
      input.addEventListener('input', () => set(input.value));
      const hex = el('span', { class: 'custom-palette-hex' }, [get()]);
      input.addEventListener('input', () => { hex.textContent = input.value; });
      return el('div', { class: 'custom-palette-row' }, [
        el('label', {}, [label]),
        input as unknown as HTMLElement,
        hex,
      ]);
    };

    root.appendChild(el('div', { class: 'sfx-tune-group' }, ['background + accents']));
    root.appendChild(colorRow('background', 'bg'));
    root.appendChild(colorRow('accent', 'accent'));
    root.appendChild(colorRow('accent 2', 'accent2'));
    root.appendChild(colorRow('danger', 'danger'));

    root.appendChild(el('div', { class: 'sfx-tune-group' }, ['piece colors']));
    root.appendChild(colorRow('i-piece', 'pieces.I'));
    root.appendChild(colorRow('o-piece', 'pieces.O'));
    root.appendChild(colorRow('t-piece', 'pieces.T'));
    root.appendChild(colorRow('s-piece', 'pieces.S'));
    root.appendChild(colorRow('z-piece', 'pieces.Z'));
    root.appendChild(colorRow('j-piece', 'pieces.J'));
    root.appendChild(colorRow('l-piece', 'pieces.L'));

    const actionRow = el('div', { class: 'row', style: 'margin-top: 18px' });
    const saveBtn = el('button', { class: 'btn primary' }, [editingIsNew ? 'save palette' : 'save changes']);
    saveBtn.addEventListener('click', () => {
      h.playMenuSfx('select');
      ctx.onSave({ ...p, pieces: { ...p.pieces } });
      editing = null;
    });
    const cancelBtn = el('button', { class: 'btn' }, ['cancel']);
    cancelBtn.addEventListener('click', () => { h.playMenuSfx('back'); editing = null; render(); });
    actionRow.appendChild(saveBtn);
    actionRow.appendChild(cancelBtn);
    root.appendChild(actionRow);
  };

  render();
  h.host.appendChild(panel);
}
